[
  {
    "key": "guideCombos",
    "path": "/guides/palworld-breeding-combos/",
    "label": "Breeding Combos Guide",
    "h1": "Palworld Breeding Combos Guide",
    "title": "Palworld Breeding Combos Guide",
    "description": "Learn Palworld breeding combos, parent-pair lookup, 1.0 caveats, route handoffs, and when to use PalCalculator fan-made breeding tools safely.",
    "keywords": "palworld breeding combos, palworld breeding combinations, palworld parent pairs, palworld breeding combos 1.0",
    "ogDescription": "Use PalCalculator to understand Palworld breeding combinations, check parent pairs, review 1.0 caveats, and move from combo ideas to calculator workflows.",
    "primaryCta": {
      "label": "Try Anubis parent lookup",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Check Penking + Bushi",
      "href": "/palworld-1-0-breeding-calculator/"
    },
    "intro": [
      "Palworld breeding combos are useful when you want a quick answer: which child comes from two parents, or which parent pairs can lead to the Pal you want. PalCalculator turns that combo intent into a calculator workflow, so you can check parent pairs, review data-version notes, and move into route planning when one direct pair is not enough.",
      "PalCalculator is an unofficial fan-made Palworld tool. Use the copy below as guidance, then verify specific parent pairs in the calculator because patches, special-combo rules, and data-source updates can change what a result means."
    ],
    "sections": [
      {
        "heading": "What are Palworld breeding combos?",
        "paragraphs": [
          "A Palworld breeding combo is a parent-pair relationship that helps you answer one of two questions: If I breed these two Pals, what child should I check? Or if I want this target Pal, which parent pairs should I review?",
          "A breeding combo is a parent-child lookup, not a full breeding plan. If you need multiple generations, use a route calculator instead of treating one combo as the whole answer."
        ]
      },
      {
        "heading": "Parent pair to child vs target child to parent pairs",
        "paragraphs": [
          "Parent pair to child mode is quickest when you already have two Pals and want to check what child the selected dataset returns.",
          "Target child to parent pairs is better for planning because it starts from your goal and shows possible combinations to investigate. A user searching for a combo list may actually need a target search, not a static chart."
        ]
      },
      {
        "heading": "How to check a combo in PalCalculator",
        "paragraphs": [
          "Open the Palworld breeding calculator, choose pair-to-child if you have two parents or target-to-parent if you know the Pal you want, then review the returned child or parent pairs with their data-version notes.",
          "If a Pal name, alias, pair, or special combo is missing, treat the result as unavailable for the current dataset rather than filling the gap with a guess."
        ]
      },
      {
        "heading": "Palworld 1.0 combo caveats",
        "paragraphs": [
          "Palworld 1.0 searches often include freshness intent. Players want to know whether a combo is current, whether special combinations are represented, and why two sites may not show the same answer.",
          "PalCalculator uses fan-made data and selected data-version notes. Different websites can disagree when they update at different times, include different special-combo assumptions, or handle missing data differently."
        ]
      },
      {
        "heading": "Current data limits before you follow a combo",
        "paragraphs": [
          "Before you follow a combo from any chart, check what the current PalCalculator dataset supports. PalCalculator is an unofficial fan-made planning site, and its results should stay tied to visible data-version notes rather than silent certainty.",
          "Current normal-formula breeding workflows are supported where the selected dataset has the required Pal data. Verified special-combo override table support is still pending, so unsupported special-combo states should be treated as unavailable instead of guessed.",
          "Use this checklist before spending resources: check the result in the current Palworld breeding calculator, look for data-version, normal-formula, special-combo, or unsupported labels, review data-source notes before following a long route, do not assume a combo guarantees passive skills, perfect IVs, or a universal best build, and recheck after patches or when another guide shows a different answer.",
          "If PalCalculator does not support a special combo or route state yet, the safe answer is unavailable in the current dataset, not a guessed combo."
        ]
      },
      {
        "heading": "Example combo workflows",
        "paragraphs": [
          "Use examples as workflows, not as unsupported exact combo claims. Check two parents to see the returned child, search targets like Anubis or Orserk for possible parents, and verify chart results before spending resources.",
          "If a result looks unexpected, compare data-version notes and special-combo caveats instead of assuming a static chart is always current."
        ]
      },
      {
        "heading": "When to use the route calculator instead",
        "paragraphs": [
          "A single breeding combo is not enough when you need a multi-step path. Use the breeding route calculator when you do not own either direct parent, want the shortest practical path from owned Pals, or need missing-Pal and alternative-route notes."
        ]
      },
      {
        "heading": "Data sources and update policy",
        "paragraphs": [
          "Before following a long combo plan, review the data sources and update policy. PalCalculator keeps version caveats visible so unsupported states are shown rather than guessed."
        ]
      }
    ],
    "links": [
      {
        "label": "Palworld breeding calculator",
        "href": "/breeding-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "breeding route calculator",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "passive skill planner",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "data sources and update policy",
        "href": "/data-sources/"
      },
      {
        "label": "Anubis breeding workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Jetragon breeding workflow",
        "href": "/guides/how-to-breed-jetragon-palworld/"
      },
      {
        "label": "passive skills guide",
        "href": "/guides/best-passive-skills-for-breeding-palworld/"
      },
      {
        "label": "how to choose useful combos",
        "href": "/guides/best-palworld-breeding-combos/"
      },
      {
        "label": "Palworld breeding FAQ",
        "href": "/guides/palworld-breeding-faq/"
      },
      {
        "label": "Orserk breeding workflow",
        "href": "/guides/how-to-breed-orserk-palworld/"
      },
      {
        "label": "Shadowbeak breeding workflow",
        "href": "/guides/how-to-breed-shadowbeak-palworld/"
      }
    ],
    "faqs": [
      {
        "question": "What is a breeding combo in Palworld?",
        "answer": "A breeding combo is a parent-pair relationship used to check a child result or find possible parents for a target Pal. It is useful for direct lookups, but it is not the same as a full multi-generation breeding route."
      },
      {
        "question": "Is PalCalculator an official Palworld breeding combo source?",
        "answer": "No. PalCalculator is an unofficial fan-made calculator and guide site. Use its visible data-version notes and caveats when checking any result."
      },
      {
        "question": "Does PalCalculator include Palworld 1.0 breeding combos?",
        "answer": "PalCalculator is built around selected Palworld 1.0 breeding workflows where the current dataset supports them. Check the 1.0 calculator and data-source notes before relying on a specific combo."
      },
      {
        "question": "Why do different Palworld breeding combo charts disagree?",
        "answer": "Charts can differ because of patch timing, data-source choices, special-combo handling, aliases, or missing data. When results disagree, verify the pair in a calculator that shows version and caveat notes."
      },
      {
        "question": "Can a breeding combo guarantee passive skills?",
        "answer": "No. A parent pair can help with breeding planning, but passive inheritance can involve RNG and supported-data limits. Use the passive skill planner for caveated guidance instead of treating a combo as a certainty."
      },
      {
        "question": "Should I use a combo list or a breeding route calculator?",
        "answer": "Use a combo list or breeding calculator for direct parent-child questions. Use a route calculator when you need multiple generations, owned-Pal planning, missing-Pal notes, or alternatives."
      },
      {
        "question": "What should I do if a Pal or combo is missing?",
        "answer": "Check spelling, use selector suggestions, remove filters, and review the data-source notes. If the current dataset does not support the result, the page should show an unavailable state rather than inventing an answer."
      }
    ]
  },
  {
    "key": "guideTree",
    "path": "/guides/palworld-breeding-tree/",
    "label": "Breeding Tree Guide",
    "h1": "Palworld Breeding Tree Guide",
    "title": "Palworld Breeding Tree Guide",
    "description": "Understand Palworld breeding trees, route steps, owned-Pal paths, missing-parent notes, and how PalCalculator finds caveated breeding routes.",
    "keywords": "palworld breeding tree, palworld breeding tree calculator, palworld breeding route, palworld breeding path",
    "ogDescription": "Learn how Palworld breeding trees work, when a direct combo becomes a route, and how to use PalCalculator's route solver with owned-Pal and data-version caveats.",
    "primaryCta": {
      "label": "Try the Anubis route demo",
      "href": "/breeding-route-calculator/"
    },
    "secondaryCta": {
      "label": "Check direct parent pairs",
      "href": "/breeding-calculator/"
    },
    "intro": [
      "A Palworld breeding tree is a route, not just one combo. Instead of checking a single parent pair, a tree shows how multiple breeding steps can connect the Pals you already have to the target Pal you want.",
      "PalCalculator is an unofficial fan-made Palworld calculator. Its route tools are designed to help you plan with visible constraints, missing-Pal notes, and data-version caveats rather than hiding uncertainty behind a static chart."
    ],
    "sections": [
      {
        "heading": "What a Palworld breeding tree means",
        "paragraphs": [
          "A breeding tree is a step-by-step path from available parents to a target Pal. Each step produces a child that may become a parent in the next generation.",
          "Use a breeding tree when the important question is the path. Use a direct breeding combo when the important question is one parent pair."
        ]
      },
      {
        "heading": "Direct combo vs multi-generation route",
        "paragraphs": [
          "A direct combo can be enough when you already own both parents and only need to verify the child result. A multi-generation route becomes useful when one parent is missing, several intermediate Pals are required, or you want to compare practical paths."
        ]
      },
      {
        "heading": "How PalCalculator builds route steps",
        "paragraphs": [
          "The breeding route calculator takes constraints such as target Pal, owned Pals, max generations, and the current dataset, then searches for supported paths.",
          "Route output should make generation count, missing Pals, alternatives, and caveats readable without needing a separate chart."
        ]
      },
      {
        "heading": "Example route-tree format",
        "paragraphs": [
          "A safe route-tree format includes target Pal, selected data version, constraints, route status, and ordered steps showing parents, child, and caveat notes.",
          "Do not insert unsupported exact Pal routes unless they are generated from current app data. Visible examples should explain the format, not pretend to be a verified route."
        ]
      },
      {
        "heading": "When a route may not be available",
        "paragraphs": [
          "A route may not appear when the target name is not matched, the generation limit is too strict, the owned-Pal list is too small, filters exclude needed parents, or data is missing or unsupported.",
          "Try increasing max generations, checking Pal names, adding more owned Pals, or reviewing the data-source notes."
        ]
      },
      {
        "heading": "Passive and IV caveats inside breeding trees",
        "paragraphs": [
          "A breeding tree can tell you a possible path, but it should not promise ideal passives or perfect stats. Passive inheritance can involve RNG, and IV/stat planning depends on supported formulas, observed stats, and modifiers."
        ]
      },
      {
        "heading": "Next steps after finding a route",
        "paragraphs": [
          "After finding a route, open the passive skill planner for desired-passive assumptions, use the IV calculator before keeping breeders, and recheck data-source notes after patches."
        ]
      }
    ],
    "links": [
      {
        "label": "Palworld breeding route calculator",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "direct breeding combo calculator",
        "href": "/breeding-calculator/"
      },
      {
        "label": "passive skill planner",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "IV calculator",
        "href": "/iv-calculator/"
      },
      {
        "label": "data version notes",
        "href": "/data-sources/"
      },
      {
        "label": "route examples guide",
        "href": "/guides/palworld-breeding-route-examples/"
      },
      {
        "label": "Anubis route workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Jetragon route workflow",
        "href": "/guides/how-to-breed-jetragon-palworld/"
      }
    ],
    "faqs": [
      {
        "question": "What is a Palworld breeding tree?",
        "answer": "A Palworld breeding tree is a multi-step route that shows how parent pairs can lead from available Pals to a target Pal. It is most useful when one direct combo is not enough."
      },
      {
        "question": "Is a breeding tree the same as a breeding combo?",
        "answer": "No. A combo is one parent-pair relationship, while a tree can include several generations and intermediate Pals. Use a route calculator when you need the path, not just one pair."
      },
      {
        "question": "Can PalCalculator use my owned Pals for a route?",
        "answer": "The route calculator is designed around owned-Pal planning where the current app supports it. MVP copy keeps owned-Pal inputs browser-first unless a later feature clearly states otherwise."
      },
      {
        "question": "Why does a breeding route show missing Pals?",
        "answer": "A missing-Pal note means the route may require a parent or intermediate Pal outside the list you provided. Use it to decide whether to catch, breed, trade for, or route around that missing requirement."
      },
      {
        "question": "Can a breeding tree guarantee passives?",
        "answer": "No. A breeding tree can organize the path, but passive inheritance can involve RNG and supported-data limits. Use passive planning notes as guidance, not as a certainty claim."
      },
      {
        "question": "How many generations should I allow?",
        "answer": "Start with a smaller generation limit if you want a simple route, then increase it if no path appears. Larger limits may find more options but can also produce routes that are less practical to execute."
      },
      {
        "question": "What should I do if no route appears?",
        "answer": "Check spelling, add more owned Pals, relax filters, increase max generations, and review data-version notes. If data is unsupported, PalCalculator should say that clearly instead of making up a route."
      }
    ]
  },
  {
    "key": "guideOne0",
    "path": "/guides/palworld-1-0-breeding-guide/",
    "label": "1.0 Breeding Guide",
    "h1": "Palworld 1.0 Breeding Guide",
    "title": "Palworld 1.0 Breeding Guide",
    "description": "Use this Palworld 1.0 breeding guide to check combos, routes, data-version notes, special-combo caveats, and fan-made calculator workflows safely.",
    "keywords": "palworld 1.0 breeding guide, palworld 1.0 breeding combos, palworld 1.0 breeding calculator",
    "ogDescription": "Use PalCalculator's fan-made Palworld 1.0 breeding workflows to check parent pairs, target parents, route steps, passives, and data-version caveats.",
    "primaryCta": {
      "label": "Open 1.0 breeding calculator",
      "href": "/palworld-1-0-breeding-calculator/"
    },
    "secondaryCta": {
      "label": "View data sources",
      "href": "/data-sources/"
    },
    "intro": [
      "Palworld 1.0 breeding searches usually have one hidden question: Is this information updated enough for the plan I am about to follow? This guide explains how to use PalCalculator's 1.0 breeding workflows while keeping data-version notes, unsupported states, and fan-made caveats visible.",
      "PalCalculator is an unofficial fan-made Palworld calculator. It can help you check parent pairs, target-parent options, route steps, IV/stat planning, and passive notes where the selected dataset supports those workflows."
    ],
    "sections": [
      {
        "heading": "What this guide covers",
        "paragraphs": [
          "This guide is for players who want a version-aware breeding workflow, not a static promise that every possible interaction is covered.",
          "Use the 1.0 breeding calculator for parent pairs and target parents, the route calculator for owned-Pal paths, and passive, IV, or stats calculators when the plan needs more detail."
        ]
      },
      {
        "heading": "How Palworld 1.0 breeding data is handled",
        "paragraphs": [
          "Calculator results depend on the selected Palworld data version and supported source categories. Patches can change breeding, stats, and passive behavior, so check the data-source page before relying on a long route or expensive breeding plan.",
          "Do not imply instant data refreshes, every special combo is covered, or passive outcomes are certain."
        ]
      },
      {
        "heading": "Use case 1: check parent pair results",
        "paragraphs": [
          "If you already have two parents, open the Palworld 1.0 breeding calculator, choose pair-to-child mode where available, enter both parents, and review the returned child, combo type, and data-version note.",
          "A parent-pair result is a lookup for the selected dataset. If you need a practical path from your Palbox, send the target Pal to the route calculator."
        ]
      },
      {
        "heading": "Use case 2: find parents for a target Pal",
        "paragraphs": [
          "If you know the Pal you want, use target-to-parent search instead of scanning a long combo list manually. Review candidate parent pairs and check whether any pair depends on special-combo rules."
        ]
      },
      {
        "heading": "Use case 3: plan a route from owned Pals",
        "paragraphs": [
          "A Palworld 1.0 combo can become a multi-step route quickly. Add or paste owned Pals where supported, choose the target Pal, set max generations, and review missing-Pal notes and alternatives before committing resources."
        ]
      },
      {
        "heading": "Use case 4: plan passives without overpromising RNG",
        "paragraphs": [
          "Breeding guides often blur the line between reaching a target Pal and getting ideal passives. Use the passive skill calculator after you have a target or route idea, and keep RNG caveats visible."
        ]
      },
      {
        "heading": "Data freshness, unsupported states, and corrections",
        "paragraphs": [
          "PalCalculator is maintained as a fan-made planning tool. If a result is unavailable, conflicted, or not supported by the current dataset, the page should show that state directly.",
          "Think a result is wrong? Review the data-source notes first, then use the listed correction path when available."
        ]
      },
      {
        "heading": "Common mistakes when using 1.0 breeding guides",
        "paragraphs": [
          "Avoid treating a saved combo chart as current forever, confusing a direct combo with a full route, assuming passives are solved by the same combo, ignoring unsupported states, or comparing websites without checking patch timing."
        ]
      }
    ],
    "links": [
      {
        "label": "Palworld 1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "parent-pair breeding calculator",
        "href": "/breeding-calculator/"
      },
      {
        "label": "route calculator",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "passive skill calculator",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "IV calculator",
        "href": "/iv-calculator/"
      },
      {
        "label": "stats calculator",
        "href": "/stats-calculator/"
      },
      {
        "label": "PalCalculator data sources",
        "href": "/data-sources/"
      },
      {
        "label": "Anubis breeding workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Jetragon breeding workflow",
        "href": "/guides/how-to-breed-jetragon-palworld/"
      },
      {
        "label": "passive skills guide",
        "href": "/guides/best-passive-skills-for-breeding-palworld/"
      },
      {
        "label": "Palworld IV explained",
        "href": "/guides/palworld-iv-explained/"
      }
    ],
    "faqs": [
      {
        "question": "Is PalCalculator updated for Palworld 1.0?",
        "answer": "PalCalculator includes 1.0-focused breeding workflows where the selected dataset supports them. Always check the visible data-version and data-source notes before relying on a specific result."
      },
      {
        "question": "What does PalCalculator mean by fan-made data?",
        "answer": "It means PalCalculator is an independent player-focused tool, not a rights-holder source. Results should be read with the site's version notes, source categories, and caveats."
      },
      {
        "question": "Does the 1.0 calculator include special combos?",
        "answer": "Special combos should be shown where the current dataset supports them, with clear labels and caveats. If a special combo is unsupported or missing, the tool should show that state instead of guessing."
      },
      {
        "question": "Why should I check data version before using a breeding guide?",
        "answer": "Palworld patches can change mechanics or make older guides less useful. Data-version notes help you judge whether a result fits the current calculator dataset."
      },
      {
        "question": "Can Palworld 1.0 breeding guarantee passives?",
        "answer": "No. Passive inheritance can involve RNG and supported-data limits. Use the passive skill calculator for planning guidance while keeping caveats visible."
      },
      {
        "question": "Where can I report a data correction?",
        "answer": "Use the correction path listed on the data-source page when available. If the page does not yet list a contact or issue path, treat that as a launch blocker for correction workflow copy."
      },
      {
        "question": "Which calculator should I use first?",
        "answer": "Use the 1.0 breeding calculator for direct parent-pair or target-parent questions. Use the route calculator when you need a multi-generation path from owned Pals."
      }
    ]
  },
  {
    "key": "guideIvExplained",
    "path": "/guides/palworld-iv-explained/",
    "label": "IV Explained Guide",
    "h1": "Palworld IV Explained",
    "title": "Palworld IV Explained",
    "description": "Learn what Palworld IVs mean, why stats vary, how IV calculators estimate ranges, and where formula, modifier, and data-version caveats apply.",
    "keywords": "Palworld IV explained, Palworld IV guide, Palworld IV calculator, Palworld stats calculator",
    "ogDescription": "Understand Palworld IVs, stat variation, calculator inputs, estimate ranges, and fan-made formula caveats before judging a breeder Pal.",
    "primaryCta": {
      "label": "Try level 50 Anubis IV bands",
      "href": "/iv-calculator/"
    },
    "secondaryCta": {
      "label": "Compare expected stats",
      "href": "/stats-calculator/"
    },
    "intro": [
      "Palworld IVs are hidden stat variation values that can make two Pals of the same species and level show different HP, Attack, or Defense. If you are choosing breeders, comparing combat Pals, or deciding whether to keep a rare hatch, IVs are one part of the decision.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use this page to understand the workflow, then check your own Pal in the IV calculator with the current data-version and formula caveats visible."
    ],
    "sections": [
      {
        "heading": "What IVs mean in Palworld",
        "paragraphs": [
          "IVs are hidden values that influence a Pal's final stats. In plain language, they help explain why two Pals that look similar on the surface can perform differently after level, species, passives, and other modifiers are considered.",
          "For players, IVs matter most when you are deciding whether a Pal is worth investing in. A better IV estimate can help you choose between possible breeders, avoid wasting resources on a weak candidate, and separate stat variation from passive-skill planning.",
          "Short answer block: IVs are hidden stat modifiers. They can affect how strong a Pal's HP, Attack, or Defense looks, but they should be evaluated together with level, passives, souls, condenser stars, and other modifiers."
        ]
      },
      {
        "heading": "Why two Pals can have different stats",
        "paragraphs": [
          "Two Pals can show different stats for several reasons. IVs are only one possible explanation, so do not judge a Pal by a single number without checking the surrounding context.",
          "Common stat factors include: - Species and base stats. - Current level. - Observed HP, Attack, and Defense. - Passive skills that modify stats. - Soul upgrades or other progression modifiers. - Condenser stars or similar enhancement systems where supported by the current formula. - Rounding behavior in the displayed in-game stats. - Patch changes or unsupported formula details.",
          "If one of these inputs is missing or wrong, an IV estimate can become wider, uncertain, or impossible. That is why the calculator should present estimates as ranges or caveated results where needed."
        ]
      },
      {
        "heading": "How an IV calculator estimates ranges",
        "paragraphs": [
          "An IV calculator works backward from the stats you can see. You enter the Pal, level, observed stats, and known modifiers. The calculator compares those inputs against the selected data and returns the IV values or ranges that could explain the result.",
          "Expected inputs: 1. Pal name. 2. Current level. 3. Observed HP, Attack, and Defense. 4. Known passive modifiers. 5. Known upgrades, stars, or other supported stat modifiers. 6. The selected Palworld data version.",
          "Expected output copy: This IV estimate is based on the selected dataset and the inputs provided. If the result shows a range, the current information is not enough to identify one exact value with confidence.",
          "Do not present an estimate as official proof. PalCalculator should help players make better decisions, while still showing where formula assumptions or game updates may affect the answer."
        ]
      },
      {
        "heading": "When IV estimates are uncertain",
        "paragraphs": [
          "An IV result can be uncertain even when the calculator is working correctly. Uncertainty usually means the observed inputs do not map cleanly to one exact answer.",
          "Common uncertainty states: - A passive or stat modifier is missing from the inputs. - The observed stat includes an upgrade the user did not select. - Rounding creates more than one possible IV value. - A patch changed a formula or modifier behavior. - The selected data version does not fully support the Pal or stat case. - The entered stats are impossible for the selected inputs.",
          "Helpful fallback copy: If the IV range looks too wide, recheck the Pal's level, visible stats, passives, upgrades, and selected data version. When a result is still uncertain, use the estimate as a planning signal rather than a final verdict."
        ]
      },
      {
        "heading": "IVs vs passives vs breeding routes",
        "paragraphs": [
          "IVs, passives, and breeding routes answer different questions.",
          "IVs answer: “How strong are this Pal's hidden stat values?” Passives answer: “Which traits do I want this Pal to carry?” Breeding routes answer: “How can I reach the target Pal from available parents?”",
          "For breeding, the cleanest workflow is to separate the decisions. First, find a route or parent-pair option. Next, plan desired passives without assuming inheritance is guaranteed. Then use IV and stat tools to decide which breeders or hatch results are worth keeping.",
          "This separation keeps the page useful without implying that one calculator can guarantee the perfect Pal."
        ]
      },
      {
        "heading": "Example IV-check workflow",
        "paragraphs": [
          "Use this workflow when you want to judge a Pal before keeping it as a breeder:",
          "1. Open the Palworld IV calculator at `/iv-calculator/`. 2. Select the Pal and data version. 3. Enter the Pal's level and observed stats. 4. Add known passives, upgrades, or supported modifiers. 5. Review the estimated IV range and any caveats. 6. Compare expected stats in `/stats-calculator/` if you need a practical stat view. 7. Decide whether the Pal is good enough for your breeding or combat goal."
        ]
      }
    ],
    "links": [
      {
        "label": "Palworld IV calculator",
        "href": "/iv-calculator/"
      },
      {
        "label": "stats calculator",
        "href": "/stats-calculator/"
      },
      {
        "label": "passive skill planner",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "breeding route calculator",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "passive skills guide",
        "href": "/guides/best-passive-skills-for-breeding-palworld/"
      },
      {
        "label": "formula and data caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What are IVs in Palworld?",
        "answer": "IVs are hidden stat variation values that can affect a Pal's HP, Attack, or Defense. They are one part of stat planning, alongside level, species, passives, upgrades, and formula caveats."
      },
      {
        "question": "Does PalCalculator show perfect IVs?",
        "answer": "PalCalculator can help estimate IVs from supported inputs, but it should not promise perfect certainty. Some results may show ranges because of rounding, missing modifiers, or formula assumptions."
      },
      {
        "question": "Why does my IV estimate show a range?",
        "answer": "A range usually means more than one IV value can explain the observed stats. Recheck level, passives, upgrades, displayed stats, and data version before treating the estimate as final."
      },
      {
        "question": "What inputs do I need for an IV calculator?",
        "answer": "You usually need the Pal, level, observed HP, Attack, Defense, and any known modifiers such as passives or upgrades supported by the current calculator."
      },
      {
        "question": "Are IVs more important than passives?",
        "answer": "Not always. IVs affect stat potential, while passives can change a Pal's role and performance. For breeding, evaluate IVs and passives together instead of treating one as the only factor."
      },
      {
        "question": "Can breeding guarantee better IVs?",
        "answer": "No. Breeding can be part of a stat-improvement plan, but PalCalculator should not claim guaranteed better IVs or perfect outcomes. Use IV estimates as decision support."
      },
      {
        "question": "Why should I check data-source notes for IVs?",
        "answer": "Formula assumptions, supported modifiers, and patch timing can affect IV estimates. The data-source page helps you understand what the calculator currently supports."
      }
    ]
  },
  {
    "key": "guidePassiveSkills",
    "path": "/guides/best-passive-skills-for-breeding-palworld/",
    "label": "Passive Skills Guide",
    "h1": "Best Passive Skills for Breeding in Palworld",
    "title": "Best Passive Skills for Breeding in Palworld",
    "description": "Plan Palworld breeding passives by goal, compare worker and combat skills, use PalCalculator planning tools, and keep inheritance RNG caveats clear.",
    "keywords": "best passive skills for breeding Palworld, Palworld breeding passives, Palworld passive skill calculator",
    "ogDescription": "Choose Palworld breeding passives by role, plan with PalCalculator, and avoid treating passive inheritance as guaranteed.",
    "primaryCta": {
      "label": "Try Artisan + Serious passive plan",
      "href": "/passive-skill-calculator/"
    },
    "secondaryCta": {
      "label": "Find a breeding route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "The best passive skills for breeding in Palworld depend on what you want the Pal to do. A base worker, a mount, a combat Pal, and a future breeder can all need different passive priorities.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use this guide to build a passive shortlist, then verify current passive names, route options, and caveats in the passive skill calculator and related tools."
    ],
    "sections": [
      {
        "heading": "What passive skills mean in breeding plans",
        "paragraphs": [
          "Passive skills are traits that can shape a Pal's role. In breeding plans, they are a goal layer on top of parent-pair and route planning. Reaching the target Pal is one problem; getting the desired passive set is another.",
          "A good passive plan should answer: - Which Pal are you trying to build? - What job should that Pal perform? - Which passive skills support that job? - Which parent or route options can move you toward the target? - Which caveats apply to inheritance, RNG, and current data support?",
          "Short answer block: Choose passive skills by role first. Then use breeding and route tools to see how practical the plan is for your current Palbox."
        ]
      },
      {
        "heading": "Choose passives by goal, not one universal list",
        "paragraphs": [
          "There is no safe universal “best passive” list for every Pal and every player. A useful guide should group passives by goal and avoid pretending that one build fits every situation.",
          "Goal-based planning examples: - Base work: prioritize passives that support work speed, stamina, or the specific job the Pal will do. - Combat: prioritize passives that support damage, survivability, element strategy, or the Pal's combat role. - Movement and utility: prioritize passives that support travel, mount use, stamina, or quality-of-life goals. - Breeding projects: prioritize passives you want to preserve or combine in future generations. - Early-game practicality: prioritize what you can realistically breed now instead of chasing a perfect late-game set too early.",
          "Do not publish a rigid ranking unless the list is reviewed, visible, and current. For production copy, frame the page as a planning framework that sends users into the calculator."
        ]
      },
      {
        "heading": "How to use the passive skill calculator",
        "paragraphs": [
          "Use the passive skill calculator when you have a target Pal, role, or desired passive set in mind.",
          "Workflow: 1. Open `/passive-skill-calculator/`. 2. Select or enter the target Pal where the UI supports it. 3. Add the passive skills you want to plan around. 4. Review recognized passive names and any unsupported or caveated states. 5. Move to `/breeding-calculator/` for direct parent-pair checks or `/breeding-route-calculator/` for multi-step route planning. 6. Use `/iv-calculator/` or `/stats-calculator/` when deciding whether a breeder is worth keeping.",
          "Expected tool copy: Passive planning does not guarantee inheritance. Use this planner to organize targets and caveats, then verify parent pairs, route steps, and current data support before committing resources."
        ]
      },
      {
        "heading": "Passive inheritance caveats",
        "paragraphs": [
          "Passive inheritance can involve RNG and supported-data limits. PalCalculator should never promise that a selected parent pair will always produce the exact passive set a user wants.",
          "Caveats to keep visible: - Desired passives are planning targets, not guaranteed results. - A breeding route can help reach a Pal without solving passive inheritance automatically. - Current app data may support names and planning states without calculating exact probabilities. - Patch changes can alter passive availability, names, or behavior. - A copied build from an older guide may be outdated or impractical for the user's current Palbox.",
          "Helpful inline caveat: If the calculator recognizes your desired passive set, treat that as a planning step. It is not a promise that the next egg will inherit every selected passive."
        ]
      },
      {
        "heading": "Example passive-planning workflow",
        "paragraphs": [
          "Use this workflow when you want a cleaner breeding project:",
          "1. Choose the target Pal. 2. Decide the role: base work, combat, mount, utility, or future breeder. 3. Build a short passive list for that role. 4. Check passive names and caveats in `/passive-skill-calculator/`. 5. Check direct parent pairs in `/breeding-calculator/`. 6. If you do not own the needed parents, open `/breeding-route-calculator/`. 7. After hatching candidates, use IV and stats tools before choosing which Pals to keep.",
          "Example copy block: For Anubis, one player may care about base work while another may care about combat. Start from the role, not from a universal list. Then use PalCalculator to check whether the route and passive plan are practical in the selected dataset."
        ]
      },
      {
        "heading": "Common passive-planning mistakes",
        "paragraphs": [
          "Mistake 1: Treating “best passives” as one fixed list. Better approach: Choose passives based on the Pal's role and your stage of progression.",
          "Mistake 2: Assuming desired passives are guaranteed. Better approach: Keep RNG caveats visible and plan for repeated attempts where needed.",
          "Mistake 3: Choosing passives before checking whether the breeding route is practical. Better approach: Use route planning to see whether your current Palbox can support the project.",
          "Mistake 4: Ignoring IVs and stats after getting the right passive names. Better approach: Use IV and stat calculators to decide whether the final Pal is worth investing in.",
          "Mistake 5: Copying old builds without checking data version. Better approach: Recheck passive names, data-source notes, and current tool support before committing to a long chain."
        ]
      }
    ],
    "links": [
      {
        "label": "Palworld passive skill calculator",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "breeding route calculator",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "parent-pair breeding calculator",
        "href": "/breeding-calculator/"
      },
      {
        "label": "IV calculator",
        "href": "/iv-calculator/"
      },
      {
        "label": "stats calculator",
        "href": "/stats-calculator/"
      },
      {
        "label": "Anubis breeding workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Jetragon breeding workflow",
        "href": "/guides/how-to-breed-jetragon-palworld/"
      },
      {
        "label": "passive data caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What are the best passive skills for breeding in Palworld?",
        "answer": "The best passives depend on the Pal's role. Base workers, combat Pals, mounts, utility Pals, and future breeders can all need different passive priorities."
      },
      {
        "question": "Can breeding guarantee passive skills?",
        "answer": "No. Passive inheritance can involve RNG and supported-data limits. PalCalculator can help organize a plan, but it should not promise a guaranteed passive outcome."
      },
      {
        "question": "Should I plan passives before or after choosing parents?",
        "answer": "Start with the target role and passive shortlist, then check parent pairs and routes. If the route is impractical, revise the passive plan or target timeline."
      },
      {
        "question": "Does PalCalculator calculate passive inheritance odds?",
        "answer": "Use the current tool labels and data-source notes to see what is supported. This page should not claim exact passive odds unless the app explicitly provides verified probability logic."
      },
      {
        "question": "Which tool should I use for passive planning?",
        "answer": "Start with the passive skill calculator, then use the breeding calculator for direct pairs or the breeding route calculator for multi-generation plans."
      },
      {
        "question": "How do IVs and passives interact in planning?",
        "answer": "Passives shape role and behavior, while IVs affect stat potential. For valuable breeders, check both before deciding which Pal to keep."
      },
      {
        "question": "Why should I avoid old passive build screenshots?",
        "answer": "Screenshots can become outdated when patches, data sources, or passive names change. Verify the passive names and route assumptions in the current calculator."
      }
    ]
  },
  {
    "key": "guideAnubis",
    "path": "/guides/how-to-breed-anubis-palworld/",
    "label": "Anubis Breeding Guide",
    "h1": "How to Breed Anubis in Palworld",
    "title": "How to Breed Anubis in Palworld",
    "description": "Learn how to plan Anubis breeding in Palworld with parent-pair lookup, route steps, passive caveats, and PalCalculator data-version notes safely.",
    "keywords": "how to breed Anubis Palworld, Palworld Anubis breeding, Anubis parent pairs, Anubis breeding route",
    "ogDescription": "Plan an Anubis breeding workflow with parent-pair checks, route solving, passive planning, IV checks, and data-version caveats.",
    "primaryCta": {
      "label": "Try Anubis parent lookup",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan an Anubis route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Breeding Anubis in Palworld is easier to plan when you treat it as a workflow instead of memorizing one saved combo. You may need a direct parent pair, a multi-generation route from Pals you already own, or a passive and IV plan after the first Anubis hatch.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use this guide to plan the steps, then verify current parent pairs and route availability in the calculator because patches and dataset updates can change results."
    ],
    "sections": [
      {
        "heading": "Before you plan an Anubis breeding route",
        "paragraphs": [
          "Start by deciding what question you need answered. Players searching for Anubis breeding usually fall into one of three groups.",
          "Common Anubis planning questions: - “Which parent pairs can produce Anubis in the current dataset?” - “Can I reach Anubis from the Pals I already own?” - “How should I plan passives, IVs, or stats after I can hatch Anubis?”",
          "Before following any route, check the selected data version and visible caveats. If you are using a screenshot, video, or old combo chart, treat it as a starting point only. The safer workflow is to verify the result in PalCalculator and review the data-source notes.",
          "Short answer block: To breed Anubis, use target-parent search to find current parent-pair options, then use the route calculator if you do not own the direct parents."
        ]
      },
      {
        "heading": "Find Anubis parent pairs in PalCalculator",
        "paragraphs": [
          "Use the breeding calculator when your question is about direct parent pairs.",
          "Workflow: 1. Open `/breeding-calculator/`. 2. Choose target-to-parent mode if the UI separates modes. 3. Search for Anubis as the target Pal. 4. Review available parent pairs for the selected data version. 5. Check whether any result has a special-combo, unsupported, or caveat label. 6. If you do not own either parent in a pair, send the target into route planning.",
          "Production caveat: Do not hardcode Anubis parent pairs into this guide unless they are generated from the current app dataset or separately verified before publishing. The page should teach users how to check the live calculator result."
        ]
      },
      {
        "heading": "Build an Anubis route from owned Pals",
        "paragraphs": [
          "A direct pair is not always the most practical answer. If your Palbox does not include the listed parents, use the route calculator to search for a multi-generation path.",
          "Route workflow: 1. Open `/breeding-route-calculator/`. 2. Set Anubis as the target Pal. 3. Add owned Pals where the tool supports owned-Pal planning. 4. Choose a practical max generation limit. 5. Review the route steps, missing-Pal notes, and alternatives. 6. Compare whether catching one missing parent is easier than breeding several intermediate Pals.",
          "Helpful route-result copy: This Anubis route is based on the selected dataset and your current constraints. If no route appears, try increasing max generations, adding more owned Pals, checking spelling, or reviewing data-source notes."
        ]
      },
      {
        "heading": "Plan Anubis passives without overpromising RNG",
        "paragraphs": [
          "Anubis can be useful in more than one role, so the “best” passive set depends on your goal. A base-work Anubis, combat Anubis, and future breeder Anubis may each need a different passive plan.",
          "Use `/passive-skill-calculator/` after you know the target. Build a desired passive shortlist, check recognized passive names, and keep inheritance caveats visible. A route can help you reach Anubis, but it does not guarantee that every desired passive will appear on the next hatch.",
          "Inline caveat: Treat desired passives as targets, not promises. Passive inheritance can involve RNG and supported-data limits."
        ]
      },
      {
        "heading": "Check Anubis IVs and stats before keeping breeders",
        "paragraphs": [
          "After you hatch an Anubis candidate, use IV and stats tools before investing heavily.",
          "Use `/iv-calculator/` when you want to estimate hidden stat values from observed stats. Use `/stats-calculator/` when you want to compare expected stat outcomes after level and modifiers are considered.",
          "This matters because a Pal with the desired passive names may still be less useful than another candidate with better stats for your goal. The best decision usually combines route practicality, passive planning, IV estimates, and the resources you are willing to spend."
        ]
      },
      {
        "heading": "Common mistakes when breeding Anubis",
        "paragraphs": [
          "Mistake 1: Following an outdated combo screenshot. Better approach: Verify parent pairs in the current breeding calculator and check data-source notes.",
          "Mistake 2: Confusing route availability with passive guarantees. Better approach: Use route tools for the path and passive tools for the desired trait plan.",
          "Mistake 3: Chasing a long route before checking direct pairs. Better approach: Start with target-parent search, then move to route planning only if direct options are impractical.",
          "Mistake 4: Ignoring IVs after getting Anubis. Better approach: Check IV and stat estimates before choosing long-term breeders or combat candidates.",
          "Mistake 5: Assuming every app or chart uses the same data version. Better approach: Prefer tools that show caveats, unsupported states, and update notes."
        ]
      }
    ],
    "links": [
      {
        "label": "find Anubis parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan an Anubis breeding route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "plan Anubis passive skills",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "check Anubis IVs",
        "href": "/iv-calculator/"
      },
      {
        "label": "compare Anubis stats",
        "href": "/stats-calculator/"
      },
      {
        "label": "Palworld breeding combos guide",
        "href": "/guides/palworld-breeding-combos/"
      },
      {
        "label": "breeding tree guide",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "passive skills guide",
        "href": "/guides/best-passive-skills-for-breeding-palworld/"
      },
      {
        "label": "data sources and update policy",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Anubis in Palworld?",
        "answer": "Use PalCalculator's current breeding data to check Anubis parent-pair and route availability. This guide does not hardcode exact combos because results should be verified against the selected dataset."
      },
      {
        "question": "What parents should I use to breed Anubis?",
        "answer": "Open the breeding calculator, search Anubis in target-to-parent mode, and review the parent pairs shown for the current data version. If you do not own the parents, use the route calculator next."
      },
      {
        "question": "Does PalCalculator guarantee Anubis breeding results?",
        "answer": "No. PalCalculator is an unofficial fan-made tool. Results depend on the selected data version and supported combo rules, so verify in game when updates change breeding behavior."
      },
      {
        "question": "Can I plan Anubis passives in the same tool?",
        "answer": "Use the passive skill calculator to plan desired passives, then return to breeding or route tools for parent and route checks. Passive planning should not be treated as guaranteed inheritance."
      },
      {
        "question": "Should I use a direct combo or a route for Anubis?",
        "answer": "Use a direct combo if you already own a valid parent pair. Use a route when you need multiple generations, missing-Pal notes, or alternatives from your current Palbox."
      },
      {
        "question": "How do I check Anubis IVs after breeding?",
        "answer": "Enter the Anubis candidate's observed stats, level, and known modifiers into the IV calculator. Review estimate ranges and caveats before deciding whether to keep it."
      },
      {
        "question": "Why might my Anubis result differ from another guide?",
        "answer": "Different guides can use different patch timing, special-combo assumptions, or data sources. Check the data-version notes and verify the route in the calculator."
      }
    ]
  },
  {
    "key": "guideJetragon",
    "path": "/guides/how-to-breed-jetragon-palworld/",
    "label": "Jetragon Breeding Guide",
    "h1": "How to Breed Jetragon in Palworld",
    "title": "How to Breed Jetragon in Palworld",
    "description": "Check Jetragon breeding options in Palworld with route planning, parent-pair caveats, data-version notes, and fan-made calculator links safely.",
    "keywords": "how to breed Jetragon Palworld, Palworld Jetragon breeding, Jetragon parent pairs, Jetragon breeding route",
    "ogDescription": "Use PalCalculator to check Jetragon parent-pair and route availability with legendary, special-combo, and data-version caveats visible.",
    "primaryCta": {
      "label": "Try Jetragon target lookup",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Review data sources",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Jetragon breeding searches need extra caution because players often expect one simple answer for a high-value target. Depending on the current Palworld version and the data PalCalculator supports, the right workflow may be a parent-pair check, a route search, or a clear unavailable state.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use this page to check Jetragon planning options without assuming official data, guaranteed availability, or complete special-combo coverage."
    ],
    "sections": [
      {
        "heading": "Can you breed Jetragon in the current dataset?",
        "paragraphs": [
          "The safest answer is to check the current calculator dataset instead of relying on a static claim. Jetragon availability can be sensitive to version, special-combo rules, and what the app currently supports.",
          "Use this page copy: To check whether Jetragon can be planned in PalCalculator, search Jetragon in the breeding calculator and review the returned state. If parent pairs or routes are unavailable, treat that as a current-data result rather than filling the gap with an unverified combo.",
          "Short answer block: Check Jetragon in the current PalCalculator dataset. If the calculator shows no supported pair or route, do not assume one exists from an old chart without verifying in game and in the data-source notes."
        ]
      },
      {
        "heading": "Check Jetragon parent-pair options",
        "paragraphs": [
          "Use the breeding calculator first when you want to know whether direct parent-pair options are available.",
          "Workflow: 1. Open `/breeding-calculator/`. 2. Choose target-to-parent mode if available. 3. Search for Jetragon. 4. Review whether the current dataset shows parent pairs, a caveated state, or an unavailable state. 5. Check any special-combo labels or data-version notes near the result. 6. If a pair appears but you do not own the parents, move to route planning.",
          "Production caveat: Do not add exact Jetragon parent pairs to this guide unless they are generated from the current app dataset or separately verified. For this page, the user value is the safe checking workflow and clear caveat handling."
        ]
      },
      {
        "heading": "Try a Jetragon route from owned Pals",
        "paragraphs": [
          "If direct pairs are unavailable or impractical, use `/breeding-route-calculator/` to see whether a route can be found from your current Palbox.",
          "Route workflow: 1. Set Jetragon as the target Pal. 2. Add owned Pals where owned-Pal planning is supported. 3. Set a practical max generation limit. 4. Review route steps, missing-Pal notes, and no-route states. 5. Compare whether relaxing constraints changes the result. 6. Recheck the data-source notes if the result conflicts with another guide.",
          "Helpful no-route copy: No Jetragon route is available for this dataset and constraint set. Try checking spelling, increasing max generations, adding more owned Pals, removing filters, or reviewing whether Jetragon is supported by the current breeding data."
        ]
      },
      {
        "heading": "Legendary and special-combo caveats",
        "paragraphs": [
          "Jetragon-related content should not blur normal breeding, special combos, unavailable data, and patch timing. Each state should be labeled clearly.",
          "Use these caveat categories: - Normal formula result: the selected dataset supports the relationship through standard breeding logic. - Special-combo result: the relationship depends on a special rule or override where the app supports that label. - Unsupported state: the current app does not have enough verified data to show a result. - No-route state: the chosen constraints do not produce a route in the current search. - Patch-sensitive state: players should recheck data-source notes and verify in game after updates.",
          "This language keeps the page useful for searchers without claiming official endorsement or complete legendary coverage."
        ]
      },
      {
        "heading": "Passive and IV planning after a Jetragon route",
        "paragraphs": [
          "If you find a supported Jetragon route, the next planning layer is quality. A route can help you reach the target Pal, but it does not guarantee desired passives or perfect stats.",
          "Use `/passive-skill-calculator/` to plan desired passives by role. Use `/iv-calculator/` and `/stats-calculator/` to evaluate hatch results before investing resources. Keep RNG, estimate ranges, and data-version caveats visible.",
          "Inline caveat: A route is not a promise of ideal passives or perfect IVs. Treat passive and stat tools as planning support, not guaranteed outcomes."
        ]
      },
      {
        "heading": "When the calculator cannot show a Jetragon route",
        "paragraphs": [
          "A missing result can be useful information. It may mean your constraints are too strict, your owned-Pal list is too small, the target name is not matched, or the current dataset does not support a route.",
          "Try these steps: - Recheck the spelling and selector suggestion for Jetragon. - Remove optional filters. - Increase the max generation limit. - Add more owned Pals if the tool supports owned-Pal planning. - Check `/data-sources/` for version notes and unsupported areas. - Verify in game after major updates before following a resource-heavy plan."
        ]
      }
    ],
    "links": [
      {
        "label": "check Jetragon parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "try a Jetragon route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "passive skill planner",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "IV calculator",
        "href": "/iv-calculator/"
      },
      {
        "label": "stats calculator",
        "href": "/stats-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding guide",
        "href": "/guides/palworld-1-0-breeding-guide/"
      },
      {
        "label": "breeding tree guide",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "data version notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can Jetragon be bred in Palworld?",
        "answer": "Check Jetragon in PalCalculator's current breeding dataset. This guide does not make a static yes/no claim because support can depend on version, special-combo handling, and current data coverage."
      },
      {
        "question": "Why does a Jetragon route not appear?",
        "answer": "A route may not appear because the target is unsupported, filters are too strict, the owned-Pal list is too small, max generations are limited, or the selected dataset does not contain a valid path."
      },
      {
        "question": "Does PalCalculator include special Jetragon combos?",
        "answer": "PalCalculator should show special-combo information only where the current dataset supports it. If a special combo is unsupported or missing, the page should say so instead of guessing."
      },
      {
        "question": "Can I guarantee passives when breeding Jetragon?",
        "answer": "No. A supported route can help you reach the target, but passive inheritance can involve RNG and data limits. Use the passive skill calculator for planning guidance."
      },
      {
        "question": "Should I use the breeding calculator or route calculator for Jetragon?",
        "answer": "Start with the breeding calculator to check parent-pair availability. Use the route calculator if you need a path from owned Pals or if direct pairs are impractical."
      },
      {
        "question": "How often should I recheck Jetragon breeding data?",
        "answer": "Recheck after major Palworld updates, when guides disagree, or before committing to a long breeding project. Use the data-source notes to judge version support."
      },
      {
        "question": "Is PalCalculator an official Jetragon breeding source?",
        "answer": "No. PalCalculator is an unofficial fan-made site. Always treat results as planning guidance and verify in game when data changes matter."
      }
    ]
  },
  {
    "key": "guideRouteExamples",
    "path": "/guides/palworld-breeding-route-examples/",
    "label": "Route Examples Guide",
    "h1": "Palworld Breeding Route Examples",
    "title": "Palworld Breeding Route Examples",
    "description": "See caveated Palworld breeding route examples, owned-Pal planning patterns, missing-parent notes, alternatives, and calculator workflow tips.",
    "keywords": "Palworld breeding route examples, Palworld breeding path examples, Palworld route calculator examples",
    "ogDescription": "Learn how to read Palworld breeding route examples, compare route options, and use PalCalculator without relying on unverified exact paths.",
    "primaryCta": {
      "label": "Try the route demo",
      "href": "/breeding-route-calculator/"
    },
    "secondaryCta": {
      "label": "Check direct combos",
      "href": "/guides/palworld-breeding-tree/"
    },
    "intro": [
      "Palworld breeding route examples are most useful when they teach you how to read a plan. A good example shows what a direct pair means, what a missing parent means, and how your owned-Pal list can change the route.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. The examples below are workflow patterns, not guaranteed exact routes. Generate specific routes from the current calculator dataset and verify in game when updates change breeding data."
    ],
    "sections": [
      {
        "heading": "What this page adds beyond the breeding tree guide",
        "paragraphs": [
          "The breeding tree guide explains the concept: a multi-step path from available parents to a target Pal. This page focuses on interpretation. It shows how to think about route outputs, missing-Pal notes, owned-Pal constraints, passive planning, and route comparison.",
          "Use this page when: - You understand direct combos but need help reading multi-step routes. - You want examples of route states without relying on an unverified chart. - You need to decide whether a route is practical for your current Palbox. - You want to know when a route example should stay unpublished or noindex.",
          "Short answer block: A route example should teach the workflow. It should not invent exact Pal paths unless those paths are generated from the current dataset and reviewed before publishing."
        ]
      },
      {
        "heading": "Example 1: direct pair is enough",
        "paragraphs": [
          "Example pattern: You search for a target Pal and the calculator shows a direct parent-pair option using Pals you already own.",
          "How to read it: - The route is short because no intermediate Pal is needed. - The main decision is whether the parent pair is current for the selected dataset. - Passive planning and IV checks still happen after the direct pair is found.",
          "Production route-card copy: Route status: Direct pair available. What it means: The selected dataset shows a parent-pair option for your target. Check data-version notes and any special-combo labels before breeding. Next step: Use the passive planner if you care about desired traits, then check IVs after hatching candidates."
        ]
      },
      {
        "heading": "Example 2: missing parent requires a multi-step route",
        "paragraphs": [
          "Example pattern: You want a target Pal, but the direct pair requires a parent you do not own. The route calculator may show an intermediate step that produces the missing parent first.",
          "How to read it: - A missing-Pal note is not a failure by itself. - The route may still be practical if the missing parent can be caught or bred easily. - A longer route may save time if it uses Pals already in your Palbox.",
          "Production route-card copy: Route status: Multi-step route with missing parent. What it means: The target may be reachable, but one or more required parents or intermediates are not in your owned-Pal list. Next step: Decide whether to catch the missing Pal, breed the intermediate, increase max generations, or compare another route."
        ]
      },
      {
        "heading": "Example 3: owned-Pal list changes the best route",
        "paragraphs": [
          "Example pattern: Two players search for the same target Pal and get different route suggestions because their Palboxes are different.",
          "How to read it: - The best route is not universal. - A route that is shortest for one player may require rare or missing parents for another. - Owned-Pal planning should explain why the result changes instead of presenting one static “best path.”",
          "Production route-card copy: Route status: Route depends on owned Pals. What it means: PalCalculator is using your current constraints to search for a practical path. Different inventories can produce different route suggestions. Next step: Add more owned Pals, adjust max generations, and compare missing-Pal notes before choosing a route."
        ]
      },
      {
        "heading": "Example 4: target passive planning after route selection",
        "paragraphs": [
          "Example pattern: You found a route to the target Pal, but now you want specific passive skills on the final result.",
          "How to read it: - The route solves target access, not passive certainty. - Desired passives should be planned separately. - You may need to compare parent candidates, repeat breeding attempts, or accept a less-than-perfect result.",
          "Production route-card copy: Route status: Target path found; passive plan not guaranteed. What it means: The route can help you reach the target Pal, but passive inheritance can involve RNG and supported-data limits. Next step: Open the passive skill calculator, choose desired passives by role, then use IV and stat tools to evaluate hatch results."
        ]
      },
      {
        "heading": "How to compare two route options",
        "paragraphs": [
          "When two routes appear, shortest is not always best. Compare the route as a player, not just as a graph.",
          "Comparison checklist: - Generations: fewer steps are usually simpler, but not always easier. - Missing Pals: a shorter route may require a Pal you do not own. - Practicality: consider catching, breeding, dungeon, boss, or availability constraints without assuming one route is universally easiest. - Passive plan: a slightly longer route may fit your passive goals better. - Data caveats: prefer routes with clear data-version and special-combo labels. - Resource cost: choose the route you can actually execute with your current game state.",
          "Helpful comparison copy: Choose the route that is easiest to execute, not just the route with the fewest steps. If a route depends on missing or uncertain data, verify it before spending resources."
        ]
      },
      {
        "heading": "Route examples that should stay noindex or unpublished",
        "paragraphs": [
          "Not every route example should be indexed. Thin, duplicated, placeholder, or unsupported examples can confuse players and weaken the site.",
          "Keep a route page noindex or unpublished when: - It contains placeholder Pal names instead of real reviewed content. - It copies an old chart without verifying current data. - It claims exact routes that are not generated from the current app dataset. - It duplicates the breeding tree guide without adding practical examples. - It implies guaranteed passives, perfect stats, or official endorsement. - It lacks visible caveats, internal links, FAQ, and data-source references."
        ]
      }
    ],
    "links": [
      {
        "label": "Palworld breeding route calculator",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "breeding tree guide",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "direct parent-pair calculator",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan passives after a route",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "check IVs before keeping breeders",
        "href": "/iv-calculator/"
      },
      {
        "label": "Anubis route workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Jetragon route workflow",
        "href": "/guides/how-to-breed-jetragon-palworld/"
      },
      {
        "label": "data version notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What is a Palworld breeding route example?",
        "answer": "It is a sample workflow that shows how to read route steps, direct pairs, missing-Pal notes, and planning caveats. It should not claim an exact path unless verified from the current dataset."
      },
      {
        "question": "Why does my route differ from someone else's route?",
        "answer": "Route results can change based on owned Pals, max generations, filters, data version, and supported special-combo rules. Different inventories can produce different practical paths."
      },
      {
        "question": "Can PalCalculator find routes from my owned Pals?",
        "answer": "Use the breeding route calculator for owned-Pal planning where the current app supports it. Add your available Pals and review the route, missing-Pal notes, and caveats."
      },
      {
        "question": "Why does a route show missing Pals?",
        "answer": "A missing-Pal note means the route may require a parent or intermediate Pal outside your provided list. Use that note to decide whether to catch, breed, trade for, or route around the requirement."
      },
      {
        "question": "Are route examples guaranteed to work after updates?",
        "answer": "No. Palworld updates and data-source changes can affect breeding results. Recheck routes in the calculator and verify in game when the cost of a mistake is high."
      },
      {
        "question": "Should I optimize for shortest route or desired passives?",
        "answer": "Compare both. A short route may be easier for reaching the target, while a different route may fit passive goals better. Passive inheritance still should not be treated as guaranteed."
      },
      {
        "question": "When should a route example stay unpublished?",
        "answer": "Keep it unpublished or noindex when it is a placeholder, copied from an unverified chart, missing caveats, too similar to another page, or based on unsupported exact-route claims."
      }
    ]
  },
  {
    "key": "guideBreedingFaq",
    "path": "/guides/palworld-breeding-faq/",
    "label": "Breeding FAQ",
    "h1": "Palworld Breeding FAQ",
    "title": "Palworld Breeding FAQ",
    "description": "Get quick answers about Palworld breeding combos, routes, passives, 1.0 data, owned-Pal planning, and PalCalculator fan-made caveats safely.",
    "keywords": "palworld breeding faq, palworld breeding questions, palcalculator caveats",
    "ogDescription": "Find quick, caveated answers about Palworld breeding tools, route planning, passive planning, privacy, and data-source notes.",
    "primaryCta": {
      "label": "Check parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan a route from owned Pals",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Use this FAQ when you need the short answer before choosing a calculator. PalCalculator can help you check parent pairs, plan routes from owned Pals, organize passive goals, estimate IV ranges, and review data-source notes, but each workflow has limits.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Treat every result as planning guidance tied to the selected dataset, then verify in game when a patch, special combo, or expensive breeding project makes accuracy important.",
      "Primary CTA: Check parent pairs",
      "Secondary CTA: Plan a route from owned Pals",
      "Check parent pairs: `/breeding-calculator/`",
      "Plan a route from owned Pals: `/breeding-route-calculator/`"
    ],
    "sections": [
      {
        "heading": "Fast answers before you choose a tool",
        "paragraphs": [
          "Short answer: start with the question you are trying to answer. Use the breeding calculator for direct parent-pair checks, the route calculator when one pair is not enough, the passive planner for trait goals, and the IV or stats tools after you have candidates to compare.",
          "This page is intentionally short-answer first. It should not duplicate the full breeding combos, breeding tree, passive skills, IV, Anubis, Jetragon, or route examples guides. Each answer should route the user to the best next tool."
        ]
      },
      {
        "heading": "What PalCalculator can help with",
        "paragraphs": [
          "PalCalculator is useful for planning decisions, not for promising one official final answer. It can support workflows such as:",
          "Checking what a selected parent pair may produce in the current dataset.",
          "Searching possible parents for a target Pal.",
          "Planning a route from Pals you already own.",
          "Keeping passive inheritance and IV/stat caveats visible.",
          "Reviewing data-source notes before following a long breeding plan."
        ]
      },
      {
        "heading": "What to verify before following a result",
        "paragraphs": [
          "Before spending resources on a route, check three things: the selected data version, whether a result relies on special-combo support, and whether the page labels unsupported states instead of guessing.",
          "If another guide disagrees with PalCalculator, compare patch timing and source notes before assuming either source is always current."
        ]
      },
      {
        "heading": "Privacy and owned-Pal inputs",
        "paragraphs": [
          "Owned-Pal route planning is most useful when the calculator can use your current Palbox or typed list. Current MVP copy should describe those inputs as browser-local unless a later owner-approved backend clearly says otherwise.",
          "Do not imply account storage, server-side Palbox syncing, or external sharing unless the product has implemented and reviewed that behavior."
        ]
      },
      {
        "heading": "Corrections and unsupported states",
        "paragraphs": [
          "If a Pal, combo, passive, IV formula, or route looks wrong, review `/data-sources/` first. The safest product behavior is to show unavailable or unsupported states clearly rather than inventing a missing answer.",
          "Correction CTA copy:",
          "Think a result is wrong? Check the data-source notes first, then use the listed correction path when available."
        ]
      },
      {
        "heading": "Choose the right next calculator",
        "paragraphs": [
          "Use the FAQ as a routing hub: open the breeding calculator for one parent-pair question, the route calculator when owned Pals matter, the passive planner for trait goals, and the IV or stats calculators after you have candidates to compare.",
          "When a result is expensive to follow, keep the data-source notes, unsupported-state labels, and fan-made caveats visible before committing resources."
        ]
      }
    ],
    "links": [
      {
        "label": "check parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan a route from owned Pals",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "plan passive skills",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "check IV ranges",
        "href": "/iv-calculator/"
      },
      {
        "label": "data version and correction notes",
        "href": "/data-sources/"
      },
      {
        "label": "browser-local privacy notes",
        "href": "/privacy/"
      }
    ],
    "faqs": [
      {
        "question": "Is PalCalculator official?",
        "answer": "No. PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use its results as planning guidance with visible data-version and source caveats."
      },
      {
        "question": "Are Palworld breeding results guaranteed?",
        "answer": "No. Results depend on the selected dataset, supported breeding rules, patch timing, and any caveats shown near the tool output. Verify in game when the cost of a mistake is high."
      },
      {
        "question": "What is the difference between a combo and a route?",
        "answer": "A combo is one parent-pair relationship. A route can include multiple generations, missing-Pal notes, and owned-Pal constraints that help you move from what you have to the target you want."
      },
      {
        "question": "Why does the route solver ask for owned Pals?",
        "answer": "Owned Pals help the route calculator search for practical paths from your current Palbox instead of showing one static path that may not fit your game state."
      },
      {
        "question": "Does PalCalculator include special breeding combos?",
        "answer": "Special-combo information should appear only where the current dataset supports it. If a special combo is unsupported or missing, PalCalculator should show that state instead of guessing."
      },
      {
        "question": "Can breeding guarantee passive skills?",
        "answer": "No. Passive skills can involve RNG and supported-data limits. Use the passive planner to organize goals, not as a promise that the next egg will inherit every selected passive."
      },
      {
        "question": "Does PalCalculator store my Palbox?",
        "answer": "Current MVP copy should frame owned-Pal inputs as browser-local unless a later reviewed feature explicitly adds account or server-side storage. Review `/privacy/` before publishing any stronger claim."
      },
      {
        "question": "How do I report incorrect data?",
        "answer": "Start with /data-sources/ to check version notes and unsupported areas. If a correction path is listed there, use it with the Pal name, expected result, source, and reproduction steps."
      }
    ]
  },
  {
    "key": "guideOrserk",
    "path": "/guides/how-to-breed-orserk-palworld/",
    "label": "Orserk Breeding Guide",
    "h1": "How to Breed Orserk in Palworld",
    "title": "How to Breed Orserk in Palworld",
    "description": "Plan Orserk breeding in Palworld with parent-pair lookup, owned-Pal routes, passive caveats, data-version notes, and calculator handoffs safely.",
    "keywords": "how to breed Orserk Palworld, Orserk breeding, Orserk parent pairs",
    "ogDescription": "Use PalCalculator to plan an Orserk breeding workflow with parent-pair checks, route planning, passive goals, IV checks, and data caveats.",
    "primaryCta": {
      "label": "Find Orserk parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan an Orserk route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Breeding Orserk is easier to plan when you separate the job into steps: first find supported parent-pair options, then check whether a route is practical from the Pals you already own, then plan passives, IVs, and stats without turning caveats into promises.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use this guide to choose the workflow, then verify current Orserk parent pairs and route availability in the calculator because patches, special-combo handling, and data-source updates can change results.",
      "Primary CTA: Find Orserk parent pairs",
      "Secondary CTA: Plan an Orserk route",
      "Find Orserk parent pairs: `/breeding-calculator/`",
      "Plan an Orserk route: `/breeding-route-calculator/`"
    ],
    "sections": [
      {
        "heading": "Start with the Orserk question you need answered",
        "paragraphs": [
          "Players searching for Orserk usually mean one of four things: Which current parent pairs can produce Orserk?",
          "Can I reach Orserk from the Pals I already own?",
          "How should I plan passives for an Orserk project?",
          "How do I judge IVs or stats after I hatch candidates?",
          "To breed Orserk, search Orserk as the target in the breeding calculator, review current parent-pair options, then use the route calculator if the direct parents are missing or impractical."
        ]
      },
      {
        "heading": "Find Orserk parent pairs in PalCalculator",
        "paragraphs": [
          "Use `/breeding-calculator/` when your first question is direct parent-pair availability.",
          "Open `/breeding-calculator/`.",
          "Choose target-to-parent mode if the UI separates modes.",
          "Search for Orserk as the target Pal.",
          "Review parent pairs shown for the selected data version.",
          "Check caveats for normal formula support, special-combo assumptions, unsupported states, or data-source notes.",
          "If none of the direct parents are practical, move into route planning.",
          "Do not hardcode Orserk parent pairs into this guide unless they are generated from the current app dataset or separately reviewed. The page should teach users how to verify current results."
        ]
      },
      {
        "heading": "Build an Orserk route from owned Pals",
        "paragraphs": [
          "A direct pair is not always the best plan. If you do not own the listed parents, use `/breeding-route-calculator/` to search for a multi-generation path from your current Palbox.",
          "Set Orserk as the target Pal.",
          "Add owned Pals where the tool supports owned-Pal planning.",
          "Choose a max generation limit you would actually execute.",
          "Review route steps, missing-Pal notes, and no-route states.",
          "Compare whether catching one missing parent is easier than breeding several intermediates.",
          "Recheck `/data-sources/` if a result conflicts with another guide.",
          "No Orserk route is available for this dataset and constraint set. Try checking spelling, adding more owned Pals, increasing max generations, removing strict filters, or reviewing data-source notes."
        ]
      },
      {
        "heading": "Planning a “perfect Orserk” without overpromising",
        "paragraphs": [
          "A search for “perfect Orserk” usually combines several different goals. Keep them separate:",
          "Route: how to reach Orserk. Passives: which traits you want to target for the intended role. IVs: how strong a candidate's hidden stat values may be. Stats: how the candidate looks after level, passives, and supported modifiers.",
          "Do not promise a perfect Orserk outcome. PalCalculator can help organize the plan, but passive inheritance can involve RNG and IV/stat estimates can depend on supported formulas and observed inputs."
        ]
      },
      {
        "heading": "Passive and IV follow-up after Orserk breeding",
        "paragraphs": [
          "After you have an Orserk route or parent-pair option, open `/passive-skill-calculator/` to plan desired passives by role. A base-worker Orserk, combat Orserk, and future breeder Orserk may not use the same priorities.",
          "After hatching candidates, use `/iv-calculator/` and `/stats-calculator/` before choosing which Orserk to keep. A candidate with useful passives may still need stat review before you invest resources."
        ]
      },
      {
        "heading": "Common mistakes when breeding Orserk",
        "paragraphs": [
          "Mistake 1: Copying one combo from an old chart. Better approach: verify Orserk in the current calculator and check data-source notes.",
          "Mistake 2: Treating a route as a passive guarantee. Better approach: use route tools for target access and passive tools for trait planning.",
          "Mistake 3: Chasing “perfect” before checking direct parent options. Better approach: start with parent-pair lookup, then route, passives, IVs, and stats.",
          "Mistake 4: Ignoring no-result states. Better approach: check spelling, aliases, generation limits, owned-Pal inputs, and unsupported special-combo notes.",
          "Mistake 5: Assuming every guide uses the same data version. Better approach: compare patch timing and prefer sources that show caveats."
        ]
      }
    ],
    "links": [
      {
        "label": "find Orserk parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan an Orserk route from owned Pals",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "plan Orserk passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "check Orserk IV ranges",
        "href": "/iv-calculator/"
      },
      {
        "label": "compare Orserk stats",
        "href": "/stats-calculator/"
      },
      {
        "label": "breeding combos guide",
        "href": "/guides/palworld-breeding-combos/"
      },
      {
        "label": "owned-Pal route planning",
        "href": "/guides/palworld-breeding-with-owned-pals/"
      },
      {
        "label": "current data caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Orserk in Palworld?",
        "answer": "Use PalCalculator's current breeding data to check Orserk parent-pair and route availability. This guide does not make an unsupported static claim because results should be verified against the selected dataset."
      },
      {
        "question": "What parents make Orserk?",
        "answer": "Open the breeding calculator, search Orserk in target-to-parent mode, and review parent pairs shown for the current data version. If you do not own a direct pair, use the route calculator next."
      },
      {
        "question": "Can I plan perfect Orserk passives?",
        "answer": "You can plan desired passives, but PalCalculator should not promise guaranteed inheritance. Treat passive selections as targets that may require repeated attempts and current data support."
      },
      {
        "question": "Should I use a direct combo or a route for Orserk?",
        "answer": "Use a direct combo if you own a supported parent pair. Use a route when you need multiple generations, missing-Pal notes, or alternatives from your owned Pals."
      },
      {
        "question": "Why might Orserk results differ from another guide?",
        "answer": "Guides can differ because of patch timing, data-source choices, aliases, special-combo handling, or unsupported states. Check data-source notes and verify in game when the result matters."
      },
      {
        "question": "What should I do if no Orserk route appears?",
        "answer": "Recheck spelling, add more owned Pals, increase max generations, remove strict filters, and review /data-sources/ for unsupported areas before assuming a route is impossible."
      },
      {
        "question": "Which calculator should I use after hatching Orserk?",
        "answer": "Use the passive planner for trait goals, the IV calculator for hidden stat estimates, and the stats calculator when you want to compare practical outcomes."
      }
    ]
  },
  {
    "key": "guideShadowbeak",
    "path": "/guides/how-to-breed-shadowbeak-palworld/",
    "label": "Shadowbeak Breeding Guide",
    "h1": "How to Breed Shadowbeak in Palworld",
    "title": "How to Breed Shadowbeak in Palworld",
    "description": "Check Shadowbeak breeding options with parent-pair lookup, route planning, passive and IV caveats, and PalCalculator data-version notes safely.",
    "keywords": "how to breed Shadowbeak Palworld, Shadowbeak breeding, Shadowbeak parent pairs",
    "ogDescription": "Plan Shadowbeak breeding with caveated parent-pair checks, owned-Pal routes, passive goals, IV/stat review, and data-source notes.",
    "primaryCta": {
      "label": "Check Shadowbeak parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Try a Shadowbeak route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Shadowbeak breeding searches often mix direct parent-pair intent with “best Shadowbeak” build intent. The safer workflow is to separate access, route practicality, passive goals, and IV/stat review before committing resources.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use this page to check Shadowbeak planning options without assuming official data, certain passive inheritance, perfect IVs, or exhaustive special-combo data.",
      "Primary CTA: Check Shadowbeak parent pairs",
      "Secondary CTA: Try a Shadowbeak route",
      "Check Shadowbeak parent pairs: `/breeding-calculator/`",
      "Try a Shadowbeak route: `/breeding-route-calculator/`"
    ],
    "sections": [
      {
        "heading": "Choose your Shadowbeak goal first",
        "paragraphs": [
          "A useful Shadowbeak breeding plan starts with the job: Reach Shadowbeak at all.",
          "Build a combat-focused Shadowbeak.",
          "Build a mount or utility candidate where relevant.",
          "Preserve passives for a future breeder.",
          "Find the most practical route from your current Palbox.",
          "To breed Shadowbeak, check target-parent options in the breeding calculator first, then use route planning if the direct parents are unavailable or impractical."
        ]
      },
      {
        "heading": "Check Shadowbeak parent-pair options",
        "paragraphs": [
          "Use `/breeding-calculator/` when you want current parent-pair data.",
          "Open `/breeding-calculator/`.",
          "Choose target-to-parent mode if available.",
          "Search for Shadowbeak.",
          "Review whether the current dataset shows parent pairs, caveated states, or unavailable states.",
          "Check labels for normal formula support, special-combo handling, and data-source notes.",
          "Move to route planning if direct parents are missing from your current Palbox.",
          "Do not add exact Shadowbeak parent pairs unless they are generated from the current app dataset or reviewed separately. The page should teach the verification workflow."
        ]
      },
      {
        "heading": "Try a Shadowbeak route from owned Pals",
        "paragraphs": [
          "If a direct pair is not practical, use `/breeding-route-calculator/` to search from Pals you already own.",
          "Set Shadowbeak as the target Pal.",
          "Add owned Pals where supported.",
          "Pick a practical max generation limit.",
          "Review route steps, missing-Pal notes, and no-route states.",
          "Compare whether catching one missing Pal is easier than breeding multiple intermediates.",
          "Recheck `/data-sources/` if another guide shows a different route.",
          "No Shadowbeak route is available for this dataset and constraint set. Try checking spelling, increasing max generations, adding more owned Pals, removing filters, or reviewing whether current breeding data supports the path."
        ]
      },
      {
        "heading": "What “best Shadowbeak” should mean",
        "paragraphs": [
          "Do not frame “best Shadowbeak” as one universal ranking. Define best by the player's goal:",
          "Combat: prioritize combat-relevant passive and stat planning where supported. Utility: consider movement or quality-of-life goals where relevant to the player's plan. Future breeder: preserve passives or candidates that support later breeding projects. Practical route: choose a path that fits the Palbox and resources the player actually has.",
          "This keeps the page useful for searchers without claiming exact best builds, guaranteed Legend inheritance, or deterministic passive outcomes."
        ]
      },
      {
        "heading": "Passive, IV, and stats follow-up",
        "paragraphs": [
          "A route can help you reach Shadowbeak. It does not guarantee a desired passive set or perfect stat outcome.",
          "Use `/passive-skill-calculator/` after choosing the Shadowbeak role. Use `/iv-calculator/` and `/stats-calculator/` after hatching candidates to compare whether a candidate fits the plan. Keep RNG, formula, range, and data-version caveats visible."
        ]
      },
      {
        "heading": "Why Shadowbeak results can differ between guides",
        "paragraphs": [
          "Shadowbeak results may differ across guides because of: Patch timing.",
          "Different data-source choices.",
          "Normal formula versus special-combo assumptions.",
          "Alias or name handling.",
          "Unsupported states being hidden by one source and shown by another.",
          "Player inventory changing the most practical route.",
          "When sources disagree, prefer a workflow that shows data-source notes and unsupported states instead of silent certainty."
        ]
      }
    ],
    "links": [
      {
        "label": "check Shadowbeak parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "try a Shadowbeak route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "plan Shadowbeak passive skills",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "estimate Shadowbeak IV ranges",
        "href": "/iv-calculator/"
      },
      {
        "label": "compare expected Shadowbeak stats",
        "href": "/stats-calculator/"
      },
      {
        "label": "passive skills guide",
        "href": "/guides/best-passive-skills-for-breeding-palworld/"
      },
      {
        "label": "route examples guide",
        "href": "/guides/palworld-breeding-route-examples/"
      },
      {
        "label": "data version notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can Shadowbeak be bred in Palworld?",
        "answer": "Check Shadowbeak in PalCalculator's current breeding dataset. This guide avoids a static yes/no claim because support can depend on data version, special-combo handling, and current coverage."
      },
      {
        "question": "What parents make Shadowbeak?",
        "answer": "Search Shadowbeak in target-to-parent mode in the breeding calculator. Review the pairs and caveats shown for the current data version before following them."
      },
      {
        "question": "Can I breed the best Shadowbeak?",
        "answer": "You can plan toward a role-based goal, but PalCalculator should not promise a universal best build, certain passive inheritance, or perfect IVs. Define the goal first, then verify route, passives, and stats."
      },
      {
        "question": "Does a route guarantee passives?",
        "answer": "No. A route can help you reach Shadowbeak, but passive inheritance can involve RNG and supported-data limits. Use passive planning as guidance."
      },
      {
        "question": "Why did another guide show a different Shadowbeak combo?",
        "answer": "The guide may use different patch timing, data sources, special-combo assumptions, or unsupported-state handling. Check /data-sources/ and verify the result in game when needed."
      },
      {
        "question": "What should I do if no Shadowbeak route appears?",
        "answer": "Recheck spelling, add more owned Pals, increase max generations, remove optional filters, and review data-source notes before assuming the target is unreachable."
      },
      {
        "question": "Which tool should I use after a Shadowbeak route appears?",
        "answer": "Use the passive planner for desired traits, the IV calculator for hidden stat estimates, and the stats calculator for practical stat comparisons."
      }
    ]
  },
  {
    "key": "guideOwnedPals",
    "path": "/guides/palworld-breeding-with-owned-pals/",
    "label": "Owned Pals Breeding Guide",
    "h1": "Palworld Breeding With Owned Pals",
    "title": "Palworld Breeding With Owned Pals",
    "description": "Learn how to plan Palworld breeding routes from the Pals you own, compare missing parents, use route constraints, and read calculator caveats.",
    "keywords": "Palworld breeding with owned Pals, owned Pal route, Palbox breeding route",
    "ogDescription": "Use PalCalculator to plan owned-Pal breeding routes, compare missing-parent notes, protect browser-local inputs, and verify data caveats.",
    "primaryCta": {
      "label": "Open the route calculator",
      "href": "/breeding-route-calculator/"
    },
    "secondaryCta": {
      "label": "Check direct parent pairs",
      "href": "/breeding-calculator/"
    },
    "intro": [
      "Owned-Pal breeding planning starts from your current Palbox instead of one generic chart. The goal is to answer: “Given the Pals I have, what route can I actually try next?”",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use owned-Pal route planning with visible data-version caveats, browser-local privacy expectations, and no promise that every route, passive, or special combo is supported.",
      "Primary CTA: Open the route calculator",
      "Secondary CTA: Check direct parent pairs",
      "Open the route calculator: `/breeding-route-calculator/`",
      "Check direct parent pairs: `/breeding-calculator/`"
    ],
    "sections": [
      {
        "heading": "Why owned-Pal planning is different",
        "paragraphs": [
          "Static combo lists usually assume the same answer fits everyone. Owned-Pal planning is more practical because it uses the Pals you already have, the target you want, and the generation limit you are willing to follow.",
          "Use owned-Pal planning when a direct combo is not enough and you need a route that starts from your current Palbox."
        ]
      },
      {
        "heading": "What to enter into the route calculator",
        "paragraphs": [
          "Expected inputs should be clear and forgiving: Target Pal.",
          "Owned Pals or current Palbox list.",
          "Max generations.",
          "Optional filters or exclusions where supported.",
          "Current data version and visible caveats.",
          "Add the Pals you can actually use, then choose the target. A larger owned-Pal list can give the route solver more practical options, but unsupported data should still be shown as unavailable rather than guessed."
        ]
      },
      {
        "heading": "How to read missing-Pal notes",
        "paragraphs": [
          "A missing-Pal note is not always a failure. It may mean the route is possible in the current dataset but requires a parent or intermediate Pal outside your owned list.",
          "Use missing-Pal notes to decide whether to: Catch the missing Pal.",
          "Breed an intermediate first.",
          "Increase max generations.",
          "Add more owned Pals.",
          "Try a different target.",
          "Stop because the route depends on unsupported data."
        ]
      },
      {
        "heading": "Compare routes like a player, not only a graph",
        "paragraphs": [
          "Shortest route is not always the best route. Compare: Generations: fewer steps are simpler, but not always easier.",
          "Missing Pals: a short route may require a hard-to-get parent.",
          "Practical effort: catching one Pal may be easier than several breeding steps.",
          "Passive goals: a longer route may fit trait planning better.",
          "IV/stat follow-up: valuable candidates may need more review after hatching.",
          "Data caveats: avoid routes that depend on unsupported special-combo assumptions."
        ]
      },
      {
        "heading": "Privacy expectations for Palbox inputs",
        "paragraphs": [
          "Current MVP copy should keep owned-Pal inputs browser-local unless a later owner-approved backend changes that behavior. Do not imply login sync, server-side Palbox storage, or account history if those features do not exist.",
          "Your route inputs are for planning the current calculation. Review the privacy page for current storage behavior before relying on any stronger account or sync assumption."
        ]
      },
      {
        "heading": "What to try when no route appears",
        "paragraphs": [
          "No route found can be useful information. Try: Recheck the target spelling and selector suggestions.",
          "Add more owned Pals.",
          "Increase max generations.",
          "Remove strict filters.",
          "Compare direct parent pairs in `/breeding-calculator/`.",
          "Review `/data-sources/` for unsupported special-combo or dataset limits.",
          "Do not turn a no-route state into a guessed route. If the current app cannot support a path, the page should say so clearly."
        ]
      }
    ],
    "links": [
      {
        "label": "route calculator from owned Pals",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "direct parent-pair lookup",
        "href": "/breeding-calculator/"
      },
      {
        "label": "breeding tree basics",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "route example patterns",
        "href": "/guides/palworld-breeding-route-examples/"
      },
      {
        "label": "Anubis route workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Orserk route workflow",
        "href": "/guides/how-to-breed-orserk-palworld/"
      },
      {
        "label": "browser-local Palbox privacy",
        "href": "/privacy/"
      },
      {
        "label": "data version notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What does breeding with owned Pals mean?",
        "answer": "It means planning a route from the Pals you currently have instead of relying on one static combo chart. The route can change when your Palbox changes."
      },
      {
        "question": "Why should I enter owned Pals?",
        "answer": "Owned Pals help the route calculator find paths that are practical for your game state and show which parents or intermediates may still be missing."
      },
      {
        "question": "Does PalCalculator store my Palbox?",
        "answer": "Current MVP copy should describe owned-Pal inputs as browser-local unless a reviewed backend feature says otherwise. Link to `/privacy/` for the current policy."
      },
      {
        "question": "Why do two players get different routes?",
        "answer": "Routes can differ because each player has different owned Pals, generation limits, filters, and tolerance for missing parents or longer paths."
      },
      {
        "question": "Is the shortest route always best?",
        "answer": "No. A short route may require a hard-to-get missing Pal, while a longer route may use Pals you already own or fit passive goals better."
      },
      {
        "question": "What should I do if no route appears?",
        "answer": "Add more owned Pals, increase max generations, check spelling, relax filters, and review data-source notes. If the dataset does not support a path, do not guess one."
      },
      {
        "question": "Can owned-Pal route planning guarantee passives?",
        "answer": "No. Route planning helps with target access. Passive inheritance can involve RNG and supported-data limits, so use the passive planner separately."
      }
    ]
  },
  {
    "key": "guideBestCombos",
    "path": "/guides/best-palworld-breeding-combos/",
    "label": "Best Combos Guide",
    "h1": "Best Palworld Breeding Combos",
    "title": "Best Palworld Breeding Combos",
    "description": "Explore useful Palworld breeding combo ideas by goal, with 1.0 data caveats, route handoffs, and links to verify results in PalCalculator safely.",
    "keywords": "best Palworld breeding combos, useful breeding combos, Palworld combo guide",
    "ogDescription": "Choose useful Palworld breeding combos by goal, then verify parent pairs, route practicality, passives, and data caveats in PalCalculator.",
    "primaryCta": {
      "label": "Verify a parent pair",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Check routes from owned Pals",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "The best Palworld breeding combo is not one universal pair. A useful combo depends on your target, stage of progression, owned Pals, passive goals, and whether the current dataset supports the relationship.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Use this page as a decision framework, then verify exact parent pairs and routes in the calculator before spending resources.",
      "Primary CTA: Verify a parent pair",
      "Secondary CTA: Check routes from owned Pals",
      "Verify a parent pair: `/breeding-calculator/`",
      "Check routes from owned Pals: `/breeding-route-calculator/`"
    ],
    "sections": [
      {
        "heading": "Define “best” before choosing combos",
        "paragraphs": [
          "Do not publish this page as a thin top-10 chart. Define best by goal: Early or mid-game practicality.",
          "Reaching a target Pal.",
          "Route simplicity from owned Pals.",
          "Passive planning fit.",
          "IV/stat follow-up value.",
          "Data support and caveat clarity.",
          "A good breeding combo is useful for your current goal and current Palbox. Verify it in the calculator instead of trusting one universal ranking."
        ]
      },
      {
        "heading": "Early and mid-game practical combos",
        "paragraphs": [
          "For early or mid-game players, practicality matters more than theoretical perfection. A useful combo is one where the parents are realistic to obtain, the route is short enough to follow, and the target helps the next stage of play.",
          "Use exact examples only if implementation generates them from current app data and editorial review accepts the caveats. If exact examples are not reviewed, write this section as a method for choosing practical combos."
        ]
      },
      {
        "heading": "Target-Pal combos",
        "paragraphs": [
          "When the goal is a specific Pal, start with target-to-parent search instead of scanning a list.",
          "Open `/breeding-calculator/`.",
          "Search the target Pal.",
          "Review supported parent pairs for the selected data version.",
          "Check whether a pair depends on special-combo handling or unsupported data.",
          "If direct parents are missing, send the target to `/breeding-route-calculator/`.",
          "Suggested target workflows to link after implementation: Anubis breeding workflow.",
          "Orserk breeding workflow.",
          "Shadowbeak breeding workflow.",
          "Jetragon caveated workflow."
        ]
      },
      {
        "heading": "Owned-Pal route combos",
        "paragraphs": [
          "A combo that looks “best” in a chart may be impractical for your Palbox. Use owned-Pal route planning when the real question is “what can I breed from what I have?”",
          "Compare route options by generation count, missing parents, practical catching effort, passive compatibility, and data caveats. If a route depends on unsupported special-combo assumptions, do not treat it as a reviewed best combo."
        ]
      },
      {
        "heading": "Passive and IV value after a combo",
        "paragraphs": [
          "The target Pal is only one layer. After choosing a combo or route, decide whether the result fits the role you want.",
          "Use `/passive-skill-calculator/` to plan desired traits without claiming deterministic inheritance. Use `/iv-calculator/` and `/stats-calculator/` after hatching candidates to compare whether a Pal is worth keeping."
        ]
      },
      {
        "heading": "Why best-combo lists disagree",
        "paragraphs": [
          "Best-combo lists often disagree because: Palworld patches change assumptions.",
          "Sites use different source data.",
          "Some charts include special combos that another dataset does not verify.",
          "Some lists rank by combat value while others rank by accessibility.",
          "Player inventories make one route practical and another route painful.",
          "Old screenshots may omit caveats or unsupported states.",
          "When two combo lists disagree, verify the pair in PalCalculator and check the visible data-source notes. Prefer caveated, current, reviewed results over silent certainty."
        ]
      },
      {
        "heading": "Review gate before this page is indexed",
        "paragraphs": [
          "This page should be indexed only when it is differentiated from the existing breeding combos guide and does not rely on unsupported exact claims.",
          "Keep the page noindex or unpublished if: It is only a rewritten combo definition page.",
          "It contains exact combo examples that were not generated or reviewed.",
          "It claims a universal best ranking.",
          "It implies exhaustive special-combo coverage.",
          "It lacks visible fan-made and data-version caveats."
        ]
      }
    ],
    "links": [
      {
        "label": "verify a parent pair",
        "href": "/breeding-calculator/"
      },
      {
        "label": "check the route from your owned Pals",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "passive planner",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "Anubis breeding workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Orserk breeding workflow",
        "href": "/guides/how-to-breed-orserk-palworld/"
      },
      {
        "label": "Shadowbeak breeding workflow",
        "href": "/guides/how-to-breed-shadowbeak-palworld/"
      },
      {
        "label": "data-source caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What are the best Palworld breeding combos?",
        "answer": "The best combos depend on your goal, Palbox, route practicality, passive plan, and current data support. Use this page as a framework, then verify exact pairs in PalCalculator."
      },
      {
        "question": "Should I trust a top-10 breeding combo list?",
        "answer": "Treat top-10 lists as ideas, not final proof. Check patch timing, data-source notes, special-combo handling, and whether the listed pair is supported in the current calculator."
      },
      {
        "question": "Are early-game combos different from late-game combos?",
        "answer": "Yes. Early-game practicality usually values accessible parents and short routes, while late-game optimization may focus more on target roles, passives, IVs, and resources."
      },
      {
        "question": "Can PalCalculator rank the best combos automatically?",
        "answer": "This page should not claim automated rankings unless the product has reviewed, visible ranking logic. For now, frame “best” as goal-based planning and verification."
      },
      {
        "question": "Why do best-combo lists disagree?",
        "answer": "Lists can differ because of patches, source data, special-combo assumptions, ranking criteria, and player inventory. Verify the pair and caveats before following a costly plan."
      },
      {
        "question": "Can a best combo guarantee the perfect Pal?",
        "answer": "No. A combo may help reach a target, but passives, IVs, stats, and patch-sensitive data still need separate caveated planning."
      },
      {
        "question": "Should this page use ranking schema?",
        "answer": "Avoid ranking or ItemList schema unless the visible page contains a reviewed, stable, genuinely ranked list. Otherwise use Article and visible FAQ only."
      }
    ]
  },
  {
    "key": "guideBaseWorkerPassives",
    "path": "/guides/palworld-base-worker-passives/",
    "label": "Base Worker Passives Guide",
    "h1": "Palworld Base Worker Passives",
    "title": "Palworld Base Worker Passives",
    "description": "Plan Palworld base worker passives by role, compare work-speed skills, use PalCalculator planning notes, and avoid guaranteed inheritance claims.",
    "keywords": "Palworld base worker passives, best worker passives Palworld, passive planner",
    "ogDescription": "Use PalCalculator to plan base-worker passive goals by role, with limited passive seed data, inheritance caveats, and route links visible.",
    "primaryCta": {
      "label": "Open the passive planner",
      "href": "/passive-skill-calculator/"
    },
    "secondaryCta": {
      "label": "Find a base-worker route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Base worker passives are about role fit, not one universal best list. A mining worker, crafting worker, transporter, and early-game helper may need different priorities.",
      "PalCalculator is an unofficial fan-made Palworld calculator and guide site. Current passive seed data is limited, so use this page as a base-work planning framework and verify recognized passive names and caveats in the passive planner and `/data-sources/`.",
      "Primary CTA: Open the passive planner",
      "Secondary CTA: Find a base-worker route",
      "Open the passive planner: `/passive-skill-calculator/`",
      "Find a base-worker route: `/breeding-route-calculator/`"
    ],
    "sections": [
      {
        "heading": "Base-worker passives by role",
        "paragraphs": [
          "Start with the job the Pal will do in your base. Useful categories include: Work speed and base productivity.",
          "Stamina or uptime where applicable.",
          "Transport and movement utility.",
          "Early-game practicality.",
          "Late-game optimization.",
          "Future breeder value.",
          "Choose base-worker passives by the job first, then check whether the passive names and route plan are supported by the current PalCalculator data."
        ]
      },
      {
        "heading": "Current passive data limits",
        "paragraphs": [
          "The current app seed data is limited. Supported passive names such as `Artisan`, `Serious`, and `Swift` can be mentioned as recognized examples, not as a complete best list.",
          "Do not imply PalCalculator currently contains every passive, exact inheritance odds, or a deterministic way to produce the final passive set. Keep `/data-sources/` linked near passive guidance."
        ]
      },
      {
        "heading": "Work speed and productivity planning",
        "paragraphs": [
          "For crafting, handiwork, mining, gathering, or similar base tasks, users usually care about work output and time saved. Frame the guidance as planning questions:",
          "What job will this Pal perform most often?",
          "Does the current passive planner recognize the passive names you want?",
          "Is the target Pal practical to breed from your current Palbox?",
          "Are you choosing a short-term worker or a long-term optimized breeder?",
          "If examples are shown, label recognized passive names as examples only. Do not call them a complete or final ranking."
        ]
      },
      {
        "heading": "Stamina, movement, and transport utility",
        "paragraphs": [
          "Some base-worker plans care about uptime, movement, or transport behavior. Keep this section goal-based because the current data may not support a complete passive table.",
          "For transport or movement-heavy base roles, look for passive goals that match the job, then verify current passive support in the planner. If the passive name is not recognized, treat it as unsupported for the current dataset rather than assuming the calculator can plan around it."
        ]
      },
      {
        "heading": "Route to the worker before optimizing traits",
        "paragraphs": [
          "A perfect passive wishlist is not useful if the target Pal is impractical to reach. Use `/breeding-calculator/` for direct parent-pair checks and `/breeding-route-calculator/` when you need a route from owned Pals.",
          "Choose the base role.",
          "Choose one or two candidate target Pals.",
          "Check direct parent pairs.",
          "If direct pairs are missing, check route options from owned Pals.",
          "Plan recognized passives.",
          "Use IV or stats tools only if the worker's long-term value justifies extra review."
        ]
      },
      {
        "heading": "Avoid passive inheritance certainty claims",
        "paragraphs": [
          "Passive planning does not guarantee inheritance. The page should avoid exact odds, deterministic outcome claims, and “always breed this” language unless reviewed product logic supports it.",
          "Treat desired passives as targets. A supported route can help you reach a Pal, but it does not promise that every desired passive will appear on the next hatch."
        ]
      },
      {
        "heading": "How this page differs from the broad passive guide",
        "paragraphs": [
          "The existing passive skills guide explains passive planning across breeding roles. This page is narrower: it focuses on base-work decisions, worker route practicality, browser-visible caveats, and limited passive seed data.",
          "Do not duplicate the broad passive guide's general framework without adding base-specific planning questions and examples."
        ]
      }
    ],
    "links": [
      {
        "label": "base worker passive planner",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "general passive skills guide",
        "href": "/guides/best-passive-skills-for-breeding-palworld/"
      },
      {
        "label": "route to a base worker target",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "check parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "check IVs before keeping breeders",
        "href": "/iv-calculator/"
      },
      {
        "label": "compare expected stats",
        "href": "/stats-calculator/"
      },
      {
        "label": "Anubis base-work planning example",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "passive data caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What are the best passives for base workers in Palworld?",
        "answer": "The best base-worker passives depend on the role, such as work speed, uptime, transport utility, early-game practicality, or long-term optimization. Verify current passive support before treating any list as complete."
      },
      {
        "question": "Does PalCalculator include every passive skill?",
        "answer": "No. Current passive seed data is limited. Use recognized examples such as `Artisan`, `Serious`, and `Swift` only as supported examples, not as a complete best-passive list."
      },
      {
        "question": "Can breeding guarantee base-worker passives?",
        "answer": "No. Passive inheritance can involve RNG and supported-data limits. PalCalculator can help organize a plan, but it should not promise certain passive outcomes."
      },
      {
        "question": "Should I choose the Pal or passive first?",
        "answer": "Start with the base job, then choose candidate Pals and passive goals together. If the target Pal is hard to reach, revise the route or passive plan."
      },
      {
        "question": "Which calculator should I use for base-worker planning?",
        "answer": "Start with the passive skill calculator, then use the breeding calculator for direct pairs or the route calculator when you need a multi-generation path from owned Pals."
      },
      {
        "question": "Are Artisan, Serious, and Swift the full best list?",
        "answer": "No. They are currently recognized examples in the app data. Do not present them as the full passive table or universal ranking."
      },
      {
        "question": "How is this different from the general passive guide?",
        "answer": "This page focuses specifically on base-worker roles, route practicality, and limited passive data caveats. The general passive guide covers broader breeding-passive planning."
      }
    ]
  },
  {
    "key": "guideBlazamut",
    "path": "/guides/how-to-breed-blazamut-palworld/",
    "label": "Blazamut Breeding Guide",
    "h1": "How to Breed Blazamut in Palworld",
    "title": "How to Breed Blazamut in Palworld",
    "description": "Plan Blazamut breeding in Palworld with parent-pair lookup, route checks, Blazamut Ryu caveats, passive follow-up, and data-source notes safely.",
    "keywords": "how to breed Blazamut Palworld, Palworld Blazamut breeding, Blazamut parent pairs, Blazamut breeding route",
    "ogDescription": "Use PalCalculator to plan Blazamut breeding with parent-pair lookup, owned-Pal route checks, Blazamut Ryu caveats, and visible data-version notes.",
    "primaryCta": {
      "label": "Check Blazamut parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan a Blazamut route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Breeding Blazamut in Palworld is easier to plan when you verify the current parent-pair options instead of relying on one saved chart. Start with Blazamut as the target, then move into route planning if the direct parents are missing from your Palbox.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this page as a safe workflow for checking current Blazamut breeding options, version notes, and caveats before spending resources."
    ],
    "sections": [
      {
        "heading": "Start with the Blazamut result you need",
        "paragraphs": [
          "Players searching for Blazamut usually need one of three answers: which parent pairs are available in the current dataset, whether a route can be built from owned Pals, or what to do after the first hatch for passives, IVs, and stats.",
          "Short answer: search Blazamut as the target in the breeding calculator, review the parent-pair results and caveats, then use the route calculator if you do not own a practical direct pair."
        ]
      },
      {
        "heading": "Check current Blazamut parent pairs",
        "paragraphs": [
          "Open `/breeding-calculator/`, choose target-to-parent mode if the UI separates modes, and search for Blazamut. Review the parent pairs shown for the selected data version, then check any labels for normal-formula support, special-combo assumptions, unsupported states, or data-source notes.",
          "Do not treat this page as a static combo table. Exact Blazamut pairs should be generated from current app data during implementation or left for users to verify in the calculator."
        ]
      },
      {
        "heading": "When to use a route instead of one direct pair",
        "paragraphs": [
          "A direct pair is useful only when you can actually execute it. If one or both parents are missing, open `/breeding-route-calculator/`, set Blazamut as the target, add owned Pals where supported, choose a max generation limit, and review route steps, missing-Pal notes, and alternatives.",
          "A route should be read as a planning result for the current data and constraints. If no route appears, try checking spelling, increasing max generations, adding more owned Pals, or reviewing `/data-sources/` for unsupported areas."
        ]
      },
      {
        "heading": "Blazamut vs Blazamut Ryu caveat",
        "paragraphs": [
          "Blazamut and Blazamut Ryu should be treated as separate targets. If you want Blazamut Ryu, search for that exact target in the calculator instead of assuming the base Blazamut page covers the variant.",
          "Variant searches can be patch-sensitive, so keep target names, aliases, and data-source notes visible when another guide or screenshot gives a different result."
        ]
      },
      {
        "heading": "Passive and IV follow-up after Blazamut breeding",
        "paragraphs": [
          "After you have a Blazamut parent-pair or route plan, use `/passive-skill-calculator/` to organize desired traits by role. Treat desired passives as planning targets, not certain outcomes.",
          "After hatching candidates, use `/iv-calculator/` and `/stats-calculator/` to compare hidden stat estimates and practical stat outcomes before investing heavily in one Blazamut."
        ]
      },
      {
        "heading": "Why another guide may show different Blazamut combos",
        "paragraphs": [
          "Different guides can disagree because of patch timing, data-source choices, special-combo handling, aliases, or unsupported states. Prefer results that show data version and caveats instead of silently presenting one chart as always current.",
          "If PalCalculator does not support a Blazamut result yet, the safe product copy should say unavailable in the current dataset rather than filling the gap with a guessed pair."
        ]
      }
    ],
    "links": [
      {
        "label": "check Blazamut parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan a Blazamut route from owned Pals",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "breeding combos guide",
        "href": "/guides/palworld-breeding-combos/"
      },
      {
        "label": "breeding path finder workflow",
        "href": "/guides/palworld-breeding-path-finder/"
      },
      {
        "label": "plan Blazamut passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "data-source and special-combo caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Blazamut in Palworld?",
        "answer": "Use PalCalculator's current breeding data to check Blazamut parent-pair and route availability. This guide keeps exact pairs in the calculator so results stay tied to the selected dataset."
      },
      {
        "question": "What parents make Blazamut?",
        "answer": "Open the breeding calculator, search Blazamut in target-to-parent mode, and review parent pairs shown for the current data version. If the direct parents are not practical, use the route calculator next."
      },
      {
        "question": "Is Blazamut Ryu the same target as Blazamut?",
        "answer": "No. Blazamut and Blazamut Ryu should be checked as separate targets. Search the exact variant name in the calculator before following a route."
      },
      {
        "question": "What if no Blazamut route appears?",
        "answer": "Recheck the target spelling, add more owned Pals, increase max generations, remove strict filters, and review /data-sources/ for unsupported areas before assuming the route is impossible."
      },
      {
        "question": "Can a Blazamut route solve passive skills too?",
        "answer": "No. A route can help reach the target, while passive planning is a separate layer with RNG and data-support caveats. Use the passive skill calculator after route planning."
      },
      {
        "question": "Does this Blazamut guide publish exact parent pairs?",
        "answer": "No. Exact pairs should be checked in the current calculator dataset so patch timing and unsupported special-combo caveats stay visible."
      },
      {
        "question": "Where should I verify Blazamut data caveats?",
        "answer": "Review the data-source page before following a long Blazamut plan, especially when another guide or screenshot shows a different result."
      }
    ]
  },
  {
    "key": "guideAstegon",
    "path": "/guides/how-to-breed-astegon-palworld/",
    "label": "Astegon Breeding Guide",
    "h1": "How to Breed Astegon in Palworld",
    "title": "How to Breed Astegon in Palworld",
    "description": "Plan Astegon breeding in Palworld with parent-pair lookup, route planning, easiest-way caveats, missing-parent notes, and data version checks.",
    "keywords": "how to breed Astegon Palworld, Palworld Astegon breeding, Astegon parent pairs, easiest way to breed Astegon",
    "ogDescription": "Use PalCalculator to plan Astegon breeding with target-parent lookup, route planning, easiest-way caveats, and current data-source notes.",
    "primaryCta": {
      "label": "Find Astegon parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Try an Astegon route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "The easiest way to breed Astegon depends on your current Palbox, direct parent availability, max-generation settings, and the data version you are using. A copied combo may be quick for one player and impractical for another.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this page to check Astegon parent pairs, route options, and caveats without turning one workflow into a universal claim."
    ],
    "sections": [
      {
        "heading": "What “easiest way” should mean for Astegon",
        "paragraphs": [
          "For Astegon, easiest should mean the most practical workflow for the player's current constraints, not one global answer for everyone. Compare direct pair availability, owned Pals, missing parents, generation count, and data-source caveats before choosing a route.",
          "Short answer: check Astegon parent pairs first, then use route planning if none of the direct options fit your Palbox."
        ]
      },
      {
        "heading": "Find Astegon parent pairs",
        "paragraphs": [
          "Open `/breeding-calculator/`, search Astegon as the target, and review parent pairs for the selected data version. Check whether any result has normal-formula labels, special-combo caveats, unavailable states, or data-source notes.",
          "Exact Astegon pairs should be generated from the current app data during implementation or verified in the calculator by the user. This handoff does not hardcode an unreviewed pair table."
        ]
      },
      {
        "heading": "Use a route when direct parents are missing",
        "paragraphs": [
          "If a direct Astegon pair requires parents you do not own, use `/breeding-route-calculator/`. Set Astegon as the target, add owned Pals where supported, choose a practical max-generation limit, and review route steps, missing-Pal notes, and alternatives.",
          "If the route looks too long, compare whether catching one missing parent is easier than breeding several intermediates. The most useful route is the one you can actually execute in your save."
        ]
      },
      {
        "heading": "How to compare Astegon route options",
        "paragraphs": [
          "Compare routes by generation count, missing Pals, parent availability, passive goals, and data caveats. A shorter route is not always more practical if it depends on a Pal you cannot get yet.",
          "Use route output as decision support. Recheck data-source notes when another guide shows a different Astegon combo or when Palworld updates change breeding assumptions."
        ]
      },
      {
        "heading": "Passive, IV, and stats follow-up",
        "paragraphs": [
          "After you can reach Astegon, decide what the final Pal is for. Use `/passive-skill-calculator/` for role-based passive planning, then use `/iv-calculator/` and `/stats-calculator/` to evaluate hatch candidates.",
          "Do not treat an Astegon parent pair as a solution for desired passives or ideal stats. Those are separate planning layers with RNG, estimate, and data-support caveats."
        ]
      },
      {
        "heading": "When Astegon results differ across sites",
        "paragraphs": [
          "Astegon combo results can differ across sites because of patch timing, special-combo handling, source choices, aliases, or missing data. Check the current PalCalculator data version and any caveat labels before committing resources.",
          "If a result is unsupported in the current dataset, the page should communicate that state clearly rather than copying a pair from another source without review."
        ]
      }
    ],
    "links": [
      {
        "label": "find Astegon parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "try an Astegon route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "breeding tree basics",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "owned-Pal route planning",
        "href": "/guides/palworld-breeding-with-owned-pals/"
      },
      {
        "label": "path finder workflow",
        "href": "/guides/palworld-breeding-path-finder/"
      },
      {
        "label": "plan Astegon passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "current data version notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What is the easiest way to breed Astegon?",
        "answer": "The easiest way depends on your owned Pals and the current data version. Start with Astegon target-parent lookup, then use route planning if direct parents are missing or impractical."
      },
      {
        "question": "What parents make Astegon?",
        "answer": "Search Astegon in the breeding calculator and review parent pairs shown for the selected dataset. This guide does not hardcode unreviewed pair lists."
      },
      {
        "question": "Should I use a direct pair or route for Astegon?",
        "answer": "Use a direct pair when you own both parents. Use a route when you need multiple generations, missing-parent notes, or alternatives from your current Palbox."
      },
      {
        "question": "Why does another Astegon guide show a different combo?",
        "answer": "Guides can differ because of patch timing, data sources, aliases, or special-combo assumptions. Check visible data-source notes before following a resource-heavy plan."
      },
      {
        "question": "Can PalCalculator plan Astegon passives?",
        "answer": "Use the passive skill calculator to organize desired traits after you choose a route or parent pair. Passive planning is separate from reaching Astegon and should keep RNG caveats visible."
      },
      {
        "question": "Does easiest mean shortest practical path for Astegon?",
        "answer": "Easiest means a practical comparison of direct parents, owned Pals, missing parents, generation count, and current caveats."
      },
      {
        "question": "Where should Astegon caveats be checked?",
        "answer": "Use the data-source page and visible calculator labels before treating an Astegon pair or route as current for your save."
      }
    ]
  },
  {
    "key": "guideGrizzbolt",
    "path": "/guides/how-to-breed-grizzbolt-palworld/",
    "label": "Grizzbolt Breeding Guide",
    "h1": "How to Breed Grizzbolt in Palworld",
    "title": "How to Breed Grizzbolt in Palworld",
    "description": "Check Grizzbolt breeding in Palworld with parent-pair lookup, owned-Pal route planning, passive and IV follow-up, plus data-source caveats safely.",
    "keywords": "how to breed Grizzbolt Palworld, Palworld Grizzbolt breeding, Grizzbolt parent pairs, Grizzbolt breeding combo",
    "ogDescription": "Use PalCalculator to check Grizzbolt parent-pair options, route planning from owned Pals, passive follow-up, IV checks, and data caveats.",
    "primaryCta": {
      "label": "Check Grizzbolt parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan a Grizzbolt route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Grizzbolt breeding searches often start with a simple combo question, but the useful answer depends on whether you own the parents, need a route, or want to plan passives after the target is reachable.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this page to check Grizzbolt parent-pair and route workflows while keeping data-version notes and unsupported-state caveats visible."
    ],
    "sections": [
      {
        "heading": "Start with the Grizzbolt question",
        "paragraphs": [
          "Most Grizzbolt searches mean one of four tasks: check current parent pairs, find a route from owned Pals, troubleshoot no-route results, or plan passives and IVs after hatching candidates.",
          "Short answer: search Grizzbolt as the target in the breeding calculator first. If the direct parents are not practical, move the target into route planning."
        ]
      },
      {
        "heading": "Check Grizzbolt parent-pair options",
        "paragraphs": [
          "Open `/breeding-calculator/`, choose target-to-parent mode if needed, and search for Grizzbolt. Review the returned parent pairs for the selected data version and check any caveat labels before following a result.",
          "Do not publish exact Grizzbolt combo claims unless implementation generates them from current app data and a reviewer accepts them. This page is designed to route users to the live calculator result."
        ]
      },
      {
        "heading": "Plan a Grizzbolt route from owned Pals",
        "paragraphs": [
          "If direct parent pairs are missing from your Palbox, use `/breeding-route-calculator/`. Set Grizzbolt as the target, add owned Pals where supported, choose max generations, and review route steps, missing-Pal notes, and no-route states.",
          "Use missing-parent notes as planning signals. Sometimes catching one missing Pal is simpler than extending the breeding chain; sometimes a longer route is more practical because it uses Pals you already have."
        ]
      },
      {
        "heading": "Troubleshoot no-route and mismatch states",
        "paragraphs": [
          "If no Grizzbolt route appears, recheck spelling, selector aliases, owned-Pal inputs, filters, and max-generation settings. Then review `/data-sources/` for current unsupported areas, especially special-combo handling and patch-sensitive data.",
          "If another guide shows a Grizzbolt combo that PalCalculator does not show, compare data versions and caveats before deciding which result to follow."
        ]
      },
      {
        "heading": "Plan Grizzbolt passives after target access",
        "paragraphs": [
          "After finding a parent pair or route, open `/passive-skill-calculator/` to organize desired traits for Grizzbolt's role. The route helps you reach the target; it does not make passive inheritance certain.",
          "Use role-based copy rather than one universal best-build claim. Combat, utility, and breeder goals may call for different passive priorities."
        ]
      },
      {
        "heading": "Check IVs and stats before keeping candidates",
        "paragraphs": [
          "Use `/iv-calculator/` to estimate hidden stat ranges from observed stats, then use `/stats-calculator/` when you want to compare practical outcomes after level and modifiers.",
          "A Grizzbolt candidate with useful passives may still need stat review. Keep IV estimates caveated where inputs, rounding, modifiers, or formulas are incomplete."
        ]
      }
    ],
    "links": [
      {
        "label": "check Grizzbolt parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan a Grizzbolt breeding route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Anubis target-Pal workflow",
        "href": "/guides/how-to-breed-anubis-palworld/"
      },
      {
        "label": "Orserk target-Pal workflow",
        "href": "/guides/how-to-breed-orserk-palworld/"
      },
      {
        "label": "plan Grizzbolt passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "check IV ranges",
        "href": "/iv-calculator/"
      },
      {
        "label": "data caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "How do you get Grizzbolt through breeding?",
        "answer": "Search Grizzbolt as the target in PalCalculator's breeding calculator, review current parent-pair options, then use the route calculator if direct parents are missing or impractical."
      },
      {
        "question": "What parents make Grizzbolt?",
        "answer": "Use target-to-parent lookup in the breeding calculator and check the results for the current data version. This page avoids hardcoding unreviewed parent pairs."
      },
      {
        "question": "What if no Grizzbolt route appears?",
        "answer": "Check spelling, owned-Pal inputs, filters, and max generations, then review /data-sources/ for unsupported states. A no-route result may be caused by constraints rather than the target alone."
      },
      {
        "question": "Can I plan Grizzbolt passives from the same route?",
        "answer": "Use the route for target access and the passive skill calculator for desired traits. Passive inheritance has RNG and data-support caveats, so treat the passive list as a plan."
      },
      {
        "question": "Why might Grizzbolt combos differ across guides?",
        "answer": "Patch timing, data-source choices, aliases, and special-combo handling can differ. Prefer pages that show current data version and caveats."
      },
      {
        "question": "Does this guide claim one best Grizzbolt combo?",
        "answer": "No. It teaches the workflow for checking current Grizzbolt parent pairs and routes instead of publishing an unreviewed universal combo."
      },
      {
        "question": "Should I verify Grizzbolt after a Palworld update?",
        "answer": "Yes. Recheck calculator results and data-source notes after major updates or when a route conflicts with another guide."
      }
    ]
  },
  {
    "key": "guideLyleen",
    "path": "/guides/how-to-breed-lyleen-palworld/",
    "label": "Lyleen Breeding Guide",
    "h1": "How to Breed Lyleen in Palworld",
    "title": "How to Breed Lyleen in Palworld",
    "description": "Plan Lyleen breeding in Palworld with parent-pair lookup, owned-Pal routes, Lyleen Noct caveats, passive planning, and data-source notes safely.",
    "keywords": "how to breed Lyleen Palworld, Palworld Lyleen breeding, Lyleen parent pairs, Lyleen Noct caveat",
    "ogDescription": "Use PalCalculator to plan Lyleen breeding with parent lookup, route planning, Lyleen Noct caveats, passive planning, and data-source notes.",
    "primaryCta": {
      "label": "Find Lyleen parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan a Lyleen route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Lyleen breeding is easiest to plan when you keep the target, route, and passive goals separate. First verify current parent pairs, then decide whether a route from owned Pals is more practical, then plan role-based passives if you care about base work or breeding projects.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this page to check Lyleen workflows with visible data-version notes, Lyleen Noct caveats, and unsupported-state language."
    ],
    "sections": [
      {
        "heading": "Choose Lyleen as the exact target",
        "paragraphs": [
          "Start by selecting Lyleen as the target in the breeding calculator. Do not merge Lyleen and Lyleen Noct results; they should be checked as distinct targets with their own target names and data states.",
          "Short answer: search Lyleen in target-to-parent mode, review current parent pairs, then use route planning if direct options are missing from your Palbox."
        ]
      },
      {
        "heading": "Find Lyleen parent pairs",
        "paragraphs": [
          "Open `/breeding-calculator/`, choose target-to-parent mode if available, and search for Lyleen. Review the parent pairs, selected data version, caveat labels, and any unsupported-state copy before following the result.",
          "Exact parent-pair examples should be generated from current app data during implementation or left to the calculator. This page should teach verification rather than publish an unreviewed combo chart."
        ]
      },
      {
        "heading": "Plan a Lyleen route from owned Pals",
        "paragraphs": [
          "If you do not own a practical direct pair, use `/breeding-route-calculator/`. Set Lyleen as the target, add owned Pals where the tool supports browser-local owned-Pal planning, pick a practical max generation limit, and review route steps and missing-Pal notes.",
          "Owned-Pal inputs should be framed as browser-local in MVP copy unless a later reviewed feature explicitly adds account or server-side storage."
        ]
      },
      {
        "heading": "Lyleen Noct and variant caveats",
        "paragraphs": [
          "Lyleen Noct deserves separate target treatment. If a player wants Lyleen Noct, send them to the calculator target selector for that exact name instead of implying this base Lyleen page covers both.",
          "Variant-sensitive pages should avoid hidden redirects, mixed data, or copied route claims. Use the data-source notes when another guide combines variants without explaining the difference."
        ]
      },
      {
        "heading": "Base work and passive planning without overclaiming",
        "paragraphs": [
          "Lyleen can connect naturally to base-worker and passive-planning content, but avoid saying one build is best for every player. Use `/passive-skill-calculator/` and `/guides/palworld-base-worker-passives/` to frame passives by role and current support.",
          "Current passive data is seed-only, so do not imply complete work-speed passive coverage or exact inheritance odds. Treat desired passives as a planning layer after target access."
        ]
      },
      {
        "heading": "IV and stat checks after Lyleen breeding",
        "paragraphs": [
          "After hatching candidates, use `/iv-calculator/` and `/stats-calculator/` if you need to compare breeder quality or role fit. IV estimates should stay caveated when inputs, formulas, modifiers, or rounding create uncertainty.",
          "A clean Lyleen plan separates target access, passive goals, and stat review instead of presenting one combo as the whole project."
        ]
      }
    ],
    "links": [
      {
        "label": "find Lyleen parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan a Lyleen route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "plan Lyleen passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "base worker passive planning",
        "href": "/guides/palworld-base-worker-passives/"
      },
      {
        "label": "breeding tree basics",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "Palworld IV explained",
        "href": "/guides/palworld-iv-explained/"
      },
      {
        "label": "PalCalculator data notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "How do you breed Lyleen in Palworld?",
        "answer": "Search Lyleen as the target in PalCalculator's breeding calculator, review current parent pairs and caveats, then use the route calculator if direct parents are not practical."
      },
      {
        "question": "What parents make Lyleen?",
        "answer": "Use target-to-parent lookup for Lyleen in the current calculator dataset. This guide does not hardcode unreviewed parent-pair lists."
      },
      {
        "question": "Is Lyleen Noct the same as Lyleen for breeding?",
        "answer": "No. Treat Lyleen and Lyleen Noct as separate targets. Search the exact variant name in the calculator before following a route."
      },
      {
        "question": "Can I use owned Pals to plan Lyleen?",
        "answer": "Use the route calculator for owned-Pal planning where supported. Current MVP copy should describe owned-Pal inputs as browser-local unless a later reviewed feature says otherwise."
      },
      {
        "question": "Which passives should I plan for Lyleen?",
        "answer": "Choose passives by role and current support. Use the passive skill calculator and base-worker guide, and avoid treating passive inheritance as certain."
      },
      {
        "question": "Does Lyleen breeding also solve base-worker stats?",
        "answer": "No. The breeding plan reaches the target; passive, IV, and stat review are separate steps with their own caveats."
      },
      {
        "question": "Should Lyleen Noct get a separate guide?",
        "answer": "Only after the exact variant page is approved. For now, this guide sends users to verify Lyleen Noct as a separate calculator target."
      }
    ]
  },
  {
    "key": "guideBreedingPathFinder",
    "path": "/guides/palworld-breeding-path-finder/",
    "label": "Breeding Path Finder Guide",
    "h1": "Palworld Breeding Path Finder",
    "title": "Palworld Breeding Path Finder",
    "description": "Use PalCalculator as a Palworld breeding path finder with owned-Pal route planning, missing-parent notes, clean constraints, and data caveats.",
    "keywords": "Palworld breeding path finder, Palworld breeding route calculator, Palworld breeding shortest path calculator, breeding path Palworld",
    "ogDescription": "Learn how to use PalCalculator as a Palworld breeding path finder with owned-Pal inputs, route constraints, missing-parent notes, and data caveats.",
    "primaryCta": {
      "label": "Open the breeding path finder",
      "href": "/breeding-route-calculator/"
    },
    "secondaryCta": {
      "label": "Check direct parent pairs first",
      "href": "/breeding-calculator/"
    },
    "intro": [
      "A Palworld breeding path finder helps when one direct combo is not enough. Instead of scanning static charts, you choose a target, add the Pals you already own where supported, set practical constraints, and read the route, missing-parent notes, or no-route state.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this guide to understand path-finder results as current-data planning output with visible limits and caveats."
    ],
    "sections": [
      {
        "heading": "What a breeding path finder does",
        "paragraphs": [
          "A breeding path finder searches for a multi-generation route from available or selected Pals to the target Pal you want. It is most useful when direct parent pairs are missing, impractical, or hard to compare manually.",
          "Short answer: use the breeding calculator for one parent-pair question and the breeding route calculator when you need a path from owned Pals, missing-parent notes, alternatives, or max-generation constraints."
        ]
      },
      {
        "heading": "Path finder vs direct parent-pair lookup",
        "paragraphs": [
          "Direct parent-pair lookup answers: which parents can produce this target, or what child comes from two selected parents. A path finder answers: how can I get from what I have to the target across one or more generations?",
          "Start with direct lookup when you already own both parents. Use the path finder when the route matters more than one combo."
        ]
      },
      {
        "heading": "How to use PalCalculator as a path finder",
        "paragraphs": [
          "Open `/breeding-route-calculator/`, choose the target Pal, add owned Pals where the tool supports browser-local owned-Pal planning, set max generations, and review the returned route status.",
          "Read each route as a constraint-based result: target, selected data version, owned-Pal assumptions, generation limit, missing parents, route steps, and caveats. If you change constraints, the route can change."
        ]
      },
      {
        "heading": "How to read missing-parent notes",
        "paragraphs": [
          "A missing-parent note is not automatically a failure. It tells you which parent or intermediate Pal may be needed outside your current owned-Pal list. Use that note to decide whether to catch the Pal, breed an intermediate, raise max generations, or compare another route.",
          "If every route depends on missing or unsupported data, review `/data-sources/` before spending resources. Unsupported states should be visible, not hidden behind a guessed path."
        ]
      },
      {
        "heading": "Shortest path wording and caveats",
        "paragraphs": [
          "Avoid saying the tool has the shortest route for every player and every patch. Safer copy: PalCalculator can search for a short route found within the current data, owned-Pal inputs, max-generation setting, and supported breeding rules.",
          "A path with fewer generations may still be less practical if it requires rare or missing parents. Compare route practicality, not just length."
        ]
      },
      {
        "heading": "Privacy and share-state boundaries",
        "paragraphs": [
          "Owned-Pal inputs should be described as browser-local unless a later reviewed product feature explicitly adds account storage or server-side syncing. Do not imply login, backend storage, or save-file upload for this page.",
          "If downstream implementation adds shareable route URLs, keep canonical indexed pages clean. Query-state, draft-state, placeholder, or user-specific URLs should stay out of the sitemap and use noindex rules where appropriate."
        ]
      },
      {
        "heading": "Next tools after a path is found",
        "paragraphs": [
          "After a route appears, use `/passive-skill-calculator/` for desired traits and `/iv-calculator/` or `/stats-calculator/` to evaluate hatch candidates. A route solves target access; it does not solve passive RNG or stat uncertainty by itself.",
          "Recheck data-source notes after major patches, when guides disagree, or before following a long route."
        ]
      }
    ],
    "links": [
      {
        "label": "open the breeding path finder",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "check direct parent pairs first",
        "href": "/breeding-calculator/"
      },
      {
        "label": "owned-Pal route planning",
        "href": "/guides/palworld-breeding-with-owned-pals/"
      },
      {
        "label": "route example patterns",
        "href": "/guides/palworld-breeding-route-examples/"
      },
      {
        "label": "breeding tree basics",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "Palworld breeding FAQ",
        "href": "/guides/palworld-breeding-faq/"
      },
      {
        "label": "route data caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "What is a Palworld breeding path finder?",
        "answer": "It is a route-planning workflow that searches for breeding paths from selected or owned Pals to a target Pal under the current data and constraints."
      },
      {
        "question": "When should I use the path finder instead of the breeding calculator?",
        "answer": "Use the breeding calculator for direct parent-pair questions. Use the path finder when you need multiple generations, owned-Pal planning, missing-parent notes, or route alternatives."
      },
      {
        "question": "Can the path finder use my owned Pals?",
        "answer": "Use owned-Pal planning where the route calculator supports it. Current MVP copy should describe those inputs as browser-local unless a reviewed feature explicitly adds account or server-side storage."
      },
      {
        "question": "Why does a path show missing parents?",
        "answer": "A missing-parent note means the route may need a parent or intermediate Pal outside your current owned-Pal list. Use it to decide whether to catch, breed, or route around that requirement."
      },
      {
        "question": "Does PalCalculator always find the shortest breeding path?",
        "answer": "No. Treat the result as a route found within current data, selected constraints, max generations, and supported rules. Compare practicality as well as route length."
      },
      {
        "question": "Should route URLs with my Palbox be indexed?",
        "answer": "No. Indexed pages should stay clean and canonical. User-specific query-state or share-state URLs should stay out of the sitemap and use noindex behavior where appropriate."
      },
      {
        "question": "What data caveats apply to the path finder?",
        "answer": "PalCalculator is an independent fan-made planning site, so route results should be treated as caveated current-data guidance."
      }
    ]
  },
  {
    "key": "guideFaleris",
    "path": "/guides/how-to-breed-faleris-palworld/",
    "label": "Faleris Breeding Guide",
    "h1": "How to Breed Faleris in Palworld",
    "title": "How to Breed Faleris in Palworld",
    "description": "Plan Faleris breeding in Palworld with parent-pair lookup, route checks, Aqua variant notes, passive follow-up, and visible data-source caveats.",
    "keywords": "how to breed Faleris Palworld, Palworld Faleris breeding, Faleris parent pairs, Faleris breed combo, Faleris Aqua caveat",
    "ogDescription": "Use PalCalculator to plan Faleris breeding with parent-pair lookup, owned-Pal route checks, Faleris Aqua caveats, and current data notes.",
    "primaryCta": {
      "label": "Check Faleris parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan a Faleris route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Breeding Faleris in Palworld is easier to plan when you verify current parent-pair options instead of following one saved combo chart. Start with Faleris as the target, then decide whether a direct pair or a route from owned Pals is the practical next step.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this page as a verification workflow for Faleris, with version notes, variant caveats, and calculator handoffs kept visible before you spend resources."
    ],
    "sections": [
      {
        "heading": "Start with Faleris as the exact target",
        "paragraphs": [
          "Choose Faleris as the target before comparing parent-pair options. Searches that mention Faleris Aqua should be treated as a separate variant intent, not silently folded into the base Faleris page.",
          "Short answer: search Faleris in the breeding calculator, review current parent-pair options and caveats, then use route planning if none of the direct pairs fit your Palbox."
        ]
      },
      {
        "heading": "Check current Faleris parent pairs",
        "paragraphs": [
          "Open `/breeding-calculator/`, use target-to-parent lookup where available, and search for Faleris. Review the returned parent pairs, the selected data version, and any caveat labels before following a result.",
          "This handoff does not publish a static Faleris combo table. Downstream implementation should either generate pairs from current app data with review or keep the page focused on calculator verification."
        ]
      },
      {
        "heading": "When to use a route from owned Pals",
        "paragraphs": [
          "If a direct pair requires parents you do not own, open `/breeding-route-calculator/`, set Faleris as the target, add owned Pals where supported, choose a practical generation limit, and review route steps plus missing-Pal notes.",
          "A route result is constraint-based. If no route appears, recheck spelling, add more owned Pals, relax filters, increase the generation limit, or review `/data-sources/` for unsupported areas."
        ]
      },
      {
        "heading": "Faleris vs Faleris Aqua caveat",
        "paragraphs": [
          "Faleris and Faleris Aqua exist as separate targets in the current dataset. If a player wants the Aqua variant, send them to search that exact target rather than implying base Faleris parent pairs apply to both.",
          "Variant pages can be added later when they have unique copy, unique internal links, and reviewed current-data behavior instead of thin duplicated sections."
        ]
      },
      {
        "heading": "Passive, IV, and stat follow-up",
        "paragraphs": [
          "After you can reach Faleris, move to `/passive-skill-calculator/` if you care about role-based traits. Desired passives are planning targets and may require repeated breeding attempts.",
          "Use `/iv-calculator/` and `/stats-calculator/` after hatching candidates to compare stat ranges and practical outcomes before choosing long-term breeders."
        ]
      },
      {
        "heading": "Why another page may show different Faleris combos",
        "paragraphs": [
          "Faleris results can differ across websites because of patch timing, source choices, variant handling, aliases, or special-combo assumptions. Prefer pages that expose data-source notes instead of hiding uncertainty.",
          "If PalCalculator cannot show a Faleris result for the current dataset or constraints, the safe page behavior is to show an unavailable state rather than copying an unreviewed pair from another source."
        ]
      }
    ],
    "links": [
      {
        "label": "check Faleris parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan a Faleris route from owned Pals",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "Blazamut target-Pal workflow",
        "href": "/guides/how-to-breed-blazamut-palworld/"
      },
      {
        "label": "breeding path finder workflow",
        "href": "/guides/palworld-breeding-path-finder/"
      },
      {
        "label": "plan Faleris passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "data-source caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Faleris in Palworld?",
        "answer": "Use PalCalculator's current breeding data to check Faleris parent-pair and route availability. This guide keeps exact pairs in the calculator so results stay tied to the selected dataset."
      },
      {
        "question": "What parents make Faleris?",
        "answer": "Open the breeding calculator, search Faleris in target-to-parent mode, and review the parent pairs shown for the current data version. Use route planning if direct parents are impractical."
      },
      {
        "question": "Is Faleris Aqua covered by this page?",
        "answer": "This page is for base Faleris. Search Faleris Aqua as a separate target before following variant-specific breeding advice."
      },
      {
        "question": "What if no Faleris route appears?",
        "answer": "Recheck spelling, add more owned Pals, increase max generations, remove strict filters, and review /data-sources/ for unsupported areas before changing the plan."
      },
      {
        "question": "Can a Faleris route solve passives too?",
        "answer": "No. A route helps with target access. Use the passive skill calculator as a separate planning layer for desired traits and current support caveats."
      }
    ]
  },
  {
    "key": "guideKitsun",
    "path": "/guides/how-to-breed-kitsun-palworld/",
    "label": "Kitsun Breeding Guide",
    "h1": "How to Breed Kitsun in Palworld",
    "title": "How to Breed Kitsun in Palworld",
    "description": "Plan Kitsun breeding in Palworld with parent lookup, route planning, Kitsun Noct variant notes, passive follow-up, and visible data-source caveats.",
    "keywords": "how to breed Kitsun Palworld, Palworld Kitsun breeding, Kitsun parent pairs, Kitsun breed combo, Kitsun Noct caveat",
    "ogDescription": "Use PalCalculator to plan Kitsun breeding with target-parent lookup, owned-Pal route planning, Kitsun Noct caveats, and current data notes.",
    "primaryCta": {
      "label": "Find Kitsun parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Try a Kitsun route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Kitsun breeding searches often mix a direct parent-pair question with variant intent. Keep the target clean: check base Kitsun first, then treat Kitsun Noct as a separate target if that is what the player wants.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this page to verify current Kitsun breeding options, route status, passive follow-up, and data-source caveats before committing resources."
    ],
    "sections": [
      {
        "heading": "Choose Kitsun instead of a mixed variant target",
        "paragraphs": [
          "Start by selecting Kitsun as the exact target. Do not assume base Kitsun and Kitsun Noct share the same parent-pair results or route behavior.",
          "Short answer: search Kitsun in target-to-parent mode, review current parent pairs, then use route planning when the direct parents are missing or inconvenient."
        ]
      },
      {
        "heading": "Find current Kitsun parent pairs",
        "paragraphs": [
          "Open `/breeding-calculator/`, search Kitsun as the target, and review parent-pair options for the selected data version. Check caveat labels before treating a pair as usable.",
          "Keep exact pair examples out of static copy unless downstream implementation generates them from current app data and review confirms the output."
        ]
      },
      {
        "heading": "Use route planning when direct parents are missing",
        "paragraphs": [
          "Open `/breeding-route-calculator/`, set Kitsun as the target, add owned Pals where supported, and compare route steps, missing-Pal notes, and no-route states.",
          "If no Kitsun route appears, change constraints deliberately: check spelling, relax filters, add more owned Pals, or increase max generations before assuming the target cannot be planned."
        ]
      },
      {
        "heading": "Kitsun Noct caveat",
        "paragraphs": [
          "Kitsun Noct exists as a distinct variant in the current dataset. Mention it only as a separate target or later page, not as hidden content inside the base Kitsun page.",
          "When another guide combines Kitsun variants, use PalCalculator's target selector and data notes to separate base and Noct results cleanly."
        ]
      },
      {
        "heading": "Passive, IV, and stats follow-up",
        "paragraphs": [
          "Once Kitsun target access is solved, use `/passive-skill-calculator/` to plan desired traits by role. Avoid implying that a parent pair automatically carries the final trait set.",
          "Use `/iv-calculator/` and `/stats-calculator/` after hatching candidates to decide which Kitsun is worth keeping for breeding, combat, or utility goals."
        ]
      },
      {
        "heading": "Common mistakes with old Kitsun combo screenshots",
        "paragraphs": [
          "Old screenshots can be useful reminders, but they may use different patch timing, aliases, or source assumptions. Verify the result in the current calculator instead of copying a static chart.",
          "If PalCalculator shows an unavailable or no-route state, keep that state visible and route the user to data-source notes rather than filling the gap with a guessed combo."
        ]
      }
    ],
    "links": [
      {
        "label": "find Kitsun parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "try a Kitsun route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Shadowbeak target-Pal workflow",
        "href": "/guides/how-to-breed-shadowbeak-palworld/"
      },
      {
        "label": "Lyleen variant caveat example",
        "href": "/guides/how-to-breed-lyleen-palworld/"
      },
      {
        "label": "owned-Pal route planning",
        "href": "/guides/palworld-breeding-with-owned-pals/"
      },
      {
        "label": "check IV ranges after hatching",
        "href": "/iv-calculator/"
      },
      {
        "label": "current data version notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Kitsun in Palworld?",
        "answer": "Check Kitsun in PalCalculator's current breeding calculator to review parent-pair and route availability for the selected dataset."
      },
      {
        "question": "What parents make Kitsun?",
        "answer": "Search Kitsun in target-to-parent mode and review the parent pairs shown in the calculator. This guide avoids static unreviewed pair lists."
      },
      {
        "question": "Is Kitsun Noct covered here?",
        "answer": "This page is for base Kitsun. Treat Kitsun Noct as a separate target and search that exact name before following variant advice."
      },
      {
        "question": "Should I use a direct pair or a route for Kitsun?",
        "answer": "Use a direct pair when you own both parents. Use the route calculator when you need multiple generations, missing-Pal notes, or alternatives from your current Palbox."
      },
      {
        "question": "Why do Kitsun combo guides differ?",
        "answer": "Guides can differ because of patch timing, data-source choices, aliases, variants, or unsupported states. Compare data notes before committing resources."
      }
    ]
  },
  {
    "key": "guideSuzaku",
    "path": "/guides/how-to-breed-suzaku-palworld/",
    "label": "Suzaku Breeding Guide",
    "h1": "How to Breed Suzaku in Palworld",
    "title": "How to Breed Suzaku in Palworld",
    "description": "Plan Suzaku breeding in Palworld with parent-pair lookup, route checks, Suzaku Aqua caveats, passive follow-up, and visible data-source notes.",
    "keywords": "how to breed Suzaku Palworld, Palworld Suzaku breeding, Suzaku parent pairs, Suzaku breed combo, Suzaku Aqua caveat",
    "ogDescription": "Use PalCalculator to plan Suzaku breeding with parent-pair lookup, route checks, Suzaku Aqua caveats, passive follow-up, and current data notes.",
    "primaryCta": {
      "label": "Check Suzaku parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan a Suzaku route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Suzaku breeding searches often come from combo charts, videos, or 1.0 freshness questions. The safer workflow is to use this page to choose the right calculator path, then verify current parent-pair or route results from the selected dataset.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this guide for Suzaku target planning, Aqua variant separation, passive follow-up, and transparent data-source caveats."
    ],
    "sections": [
      {
        "heading": "Start with the Suzaku result you need",
        "paragraphs": [
          "Select Suzaku as the target first. If the search intent is Suzaku Aqua, send the user to search that exact variant rather than mixing base and Aqua routes.",
          "Short answer: check Suzaku parent pairs in the breeding calculator, then move to route planning if the direct parents are not practical for your Palbox."
        ]
      },
      {
        "heading": "Check current Suzaku parent-pair options",
        "paragraphs": [
          "Open `/breeding-calculator/`, search Suzaku as the target, and review parent pairs with the selected data version visible. Check caveat labels before acting on a result.",
          "Do not turn this page into a static combo table unless the exact pairs are generated from current app data during implementation and reviewed before publishing."
        ]
      },
      {
        "heading": "Plan a Suzaku route when a direct pair is impractical",
        "paragraphs": [
          "Use `/breeding-route-calculator/` when you do not own a practical direct pair. Set Suzaku as the target, add owned Pals where supported, choose a max generation limit, and read route steps plus missing-Pal notes.",
          "Route results can change with constraints. If no route appears, check spelling, remove strict filters, add more owned Pals, or review `/data-sources/` for unsupported states."
        ]
      },
      {
        "heading": "Suzaku Aqua variant caveat",
        "paragraphs": [
          "Suzaku and Suzaku Aqua exist as separate targets in the current dataset. Base Suzaku copy should mention Aqua only as a variant caveat or future page opportunity.",
          "If another guide or video uses Aqua results inside base Suzaku advice, verify the exact target in PalCalculator before following the plan."
        ]
      },
      {
        "heading": "Passive and IV follow-up after target access",
        "paragraphs": [
          "After reaching a Suzaku route or parent-pair plan, use `/passive-skill-calculator/` to organize desired traits by role. Treat passive planning as separate from target access.",
          "Use `/iv-calculator/` and `/stats-calculator/` after hatching candidates when you need to compare stat ranges or practical outcomes."
        ]
      },
      {
        "heading": "How to handle Reddit or video combo mismatches",
        "paragraphs": [
          "Community posts and videos can lag behind patches or use different data assumptions. Use them as leads, then verify against PalCalculator's current dataset and data-source notes.",
          "When results disagree, preserve the mismatch as a caveat and send users to calculator verification rather than choosing an unreviewed static claim."
        ]
      }
    ],
    "links": [
      {
        "label": "check Suzaku parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan a Suzaku breeding route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding guide",
        "href": "/guides/palworld-1-0-breeding-guide/"
      },
      {
        "label": "Faleris target-Pal workflow",
        "href": "/guides/how-to-breed-faleris-palworld/"
      },
      {
        "label": "plan Suzaku passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "compare expected stats",
        "href": "/stats-calculator/"
      },
      {
        "label": "data caveats",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Suzaku in Palworld?",
        "answer": "Use PalCalculator's current breeding data to check Suzaku parent-pair and route availability for the selected dataset."
      },
      {
        "question": "What parents make Suzaku?",
        "answer": "Open the breeding calculator, search Suzaku in target-to-parent mode, and review current parent pairs plus any caveat labels."
      },
      {
        "question": "Is Suzaku Aqua the same target?",
        "answer": "No. Suzaku Aqua should be checked as its own target. Do not apply base Suzaku route copy to the Aqua variant without verification."
      },
      {
        "question": "What if another guide shows a different Suzaku combo?",
        "answer": "Compare patch timing and data-source notes, then verify the exact target in the current calculator before spending resources."
      },
      {
        "question": "What if no Suzaku route appears?",
        "answer": "Recheck spelling, add owned Pals, relax filters, increase max generations, and review /data-sources/ for unsupported areas."
      }
    ]
  },
  {
    "key": "guideHelzephyr",
    "path": "/guides/how-to-breed-helzephyr-palworld/",
    "label": "Helzephyr Breeding Guide",
    "h1": "How to Breed Helzephyr in Palworld",
    "title": "How to Breed Helzephyr in Palworld",
    "description": "Plan Helzephyr breeding in Palworld with parent lookup, route planning, Helzephyr Lux caveats, passive follow-up, and visible data-source notes.",
    "keywords": "how to breed Helzephyr Palworld, Palworld Helzephyr breeding, Helzephyr parent pairs, Helzephyr breed combo, Helzephyr Lux caveat",
    "ogDescription": "Use PalCalculator to plan Helzephyr breeding with parent lookup, route planning, Helzephyr Lux caveats, passive follow-up, and data notes.",
    "primaryCta": {
      "label": "Find Helzephyr parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Try a Helzephyr route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Helzephyr breeding is safest to plan as a current-data workflow: select Helzephyr as the target, review parent-pair options, then route from owned Pals if a direct pair is not practical.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this page to verify Helzephyr routes, keep Helzephyr Lux separate, and preserve data-source caveats before following a combo."
    ],
    "sections": [
      {
        "heading": "Select Helzephyr as the exact target",
        "paragraphs": [
          "Start with the base Helzephyr target. If the player wants Helzephyr Lux, treat that as a separate variant search and not as a hidden subsection with mixed parent pairs.",
          "Short answer: search Helzephyr in target-to-parent mode first, then use route planning if direct parents are unavailable or inconvenient."
        ]
      },
      {
        "heading": "Find current Helzephyr parent pairs",
        "paragraphs": [
          "Open `/breeding-calculator/`, search Helzephyr, and review parent pairs for the selected data version. Check labels for normal formula support, special-combo assumptions, unavailable states, and data-source notes.",
          "This copy intentionally avoids static unreviewed pair tables. Exact examples should come only from current app data during implementation and review."
        ]
      },
      {
        "heading": "Use the route calculator if direct parents are missing",
        "paragraphs": [
          "Open `/breeding-route-calculator/`, set Helzephyr as the target, add owned Pals where supported, and compare route steps, missing-Pal notes, alternatives, and no-route states.",
          "A longer route can still be practical if it uses Pals you already own. Compare generation count with missing-parent effort before choosing a plan."
        ]
      },
      {
        "heading": "Helzephyr Lux caveat",
        "paragraphs": [
          "Helzephyr Lux exists as a separate variant target in the current dataset. Mention it as a caveat or later page, but do not imply base Helzephyr pairs apply to Lux.",
          "If search demand grows for Helzephyr Lux, create a unique variant page only after base Helzephyr has shipped and the variant copy can stand on its own."
        ]
      },
      {
        "heading": "Passive, IV, and stats after a route",
        "paragraphs": [
          "Once a Helzephyr parent pair or route is available, use `/passive-skill-calculator/` to plan desired traits by role without treating inheritance as certain.",
          "Use `/iv-calculator/` and `/stats-calculator/` after hatching candidates when deciding which Helzephyr to keep or invest in."
        ]
      },
      {
        "heading": "Why combo results can differ between sites",
        "paragraphs": [
          "Different sites can update at different times, apply variant names differently, or assume unsupported special-combo data. PalCalculator should keep those caveats visible.",
          "If a Helzephyr result is missing or conflicts with another source, route users to calculator verification and `/data-sources/` instead of publishing a borrowed answer."
        ]
      }
    ],
    "links": [
      {
        "label": "find Helzephyr parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "try a Helzephyr route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "breeding tree basics",
        "href": "/guides/palworld-breeding-tree/"
      },
      {
        "label": "route example patterns",
        "href": "/guides/palworld-breeding-route-examples/"
      },
      {
        "label": "plan Helzephyr passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "check IV estimates",
        "href": "/iv-calculator/"
      },
      {
        "label": "current data notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Helzephyr in Palworld?",
        "answer": "Check Helzephyr in PalCalculator's current breeding calculator to review parent-pair and route availability for the selected dataset."
      },
      {
        "question": "What parents make Helzephyr?",
        "answer": "Use target-to-parent lookup for Helzephyr in the breeding calculator. Review current pairs and data caveats instead of relying on a copied chart."
      },
      {
        "question": "Is Helzephyr Lux the same target?",
        "answer": "No. Helzephyr Lux should be checked as a separate target before following variant-specific advice."
      },
      {
        "question": "How do I route from owned Pals to Helzephyr?",
        "answer": "Use the route calculator, add owned Pals where supported, set a practical generation limit, and compare route steps plus missing-Pal notes."
      },
      {
        "question": "Can this page choose the best Helzephyr build?",
        "answer": "No. It can route players to parent-pair, passive, IV, and stats tools, but role choices and current support should guide build planning."
      }
    ]
  },
  {
    "key": "guideSelyne",
    "path": "/guides/how-to-breed-selyne-palworld/",
    "label": "Selyne Breeding Guide",
    "h1": "How to Breed Selyne in Palworld",
    "title": "How to Breed Selyne in Palworld",
    "description": "Plan Selyne breeding in Palworld with parent-pair lookup, route checks, patch-sensitive caveats, passive follow-up, and visible data-source notes.",
    "keywords": "how to breed Selyne Palworld, Palworld Selyne breeding, Selyne parent pairs, Selyne breed combo, Palworld 1.0 Selyne breeding",
    "ogDescription": "Use PalCalculator to plan Selyne breeding with current parent-pair lookup, route planning, 1.0 caveats, passive follow-up, and data notes.",
    "primaryCta": {
      "label": "Check Selyne parent pairs",
      "href": "/breeding-calculator/"
    },
    "secondaryCta": {
      "label": "Plan a Selyne route",
      "href": "/breeding-route-calculator/"
    },
    "intro": [
      "Selyne breeding searches often carry 1.0 freshness intent, so the page should stay version-aware from the first screen. Use current calculator data first, then compare routes, caveats, and follow-up tools before committing resources.",
      "PalCalculator is an independent fan-made Palworld calculator and guide site. Use this Selyne guide to verify parent pairs, route status, patch-sensitive notes, and data-source caveats instead of relying on one combo screenshot."
    ],
    "sections": [
      {
        "heading": "Check Selyne in the current dataset first",
        "paragraphs": [
          "Start by searching Selyne as the exact target and reading the selected data version. This keeps 1.0 and patch-sensitive questions tied to visible calculator support.",
          "Short answer: use target-to-parent lookup for Selyne, then move into route planning if the direct parent options are missing or impractical."
        ]
      },
      {
        "heading": "Find current Selyne parent pairs",
        "paragraphs": [
          "Open `/breeding-calculator/`, search Selyne as the target, and review parent pairs for the current data version. Check caveat labels before following a result.",
          "Do not publish exact Selyne pair tables in this handoff. Downstream implementation should generate pairs from current app data with review or keep users in the calculator for verification."
        ]
      },
      {
        "heading": "Route planning when direct parents are missing",
        "paragraphs": [
          "Open `/breeding-route-calculator/`, set Selyne as the target, add owned Pals where supported, choose a practical generation limit, and review route steps, missing-Pal notes, and no-route states.",
          "If no Selyne route appears, recheck spelling, add more owned Pals, relax filters, increase max generations, or review `/data-sources/` before changing targets."
        ]
      },
      {
        "heading": "1.0 and patch-sensitive caveats",
        "paragraphs": [
          "Selyne pages should be explicit about the selected data version because users may be comparing 1.0-era guides and combo screenshots. Keep the data-source link near the workflow copy.",
          "If another source disagrees, treat that as a prompt to compare patch timing and calculator support rather than as proof that one static answer should replace the current-data workflow."
        ]
      },
      {
        "heading": "Passive, IV, and stats follow-up",
        "paragraphs": [
          "After you can reach Selyne, use `/passive-skill-calculator/` for desired traits by role. Passive planning should stay separate from the route itself.",
          "Use `/iv-calculator/` and `/stats-calculator/` after hatching candidates if you need to compare hidden stat ranges, expected stats, or breeder quality."
        ]
      },
      {
        "heading": "What to do when another guide differs",
        "paragraphs": [
          "When another guide shows a different Selyne combo, compare data version, variant assumptions, source timing, and caveat labels. Recheck inside PalCalculator before following an expensive plan.",
          "If PalCalculator cannot support a Selyne result for the current dataset or constraints, show the unavailable state clearly and link to data-source notes."
        ]
      }
    ],
    "links": [
      {
        "label": "check Selyne parent pairs",
        "href": "/breeding-calculator/"
      },
      {
        "label": "plan a Selyne route",
        "href": "/breeding-route-calculator/"
      },
      {
        "label": "Palworld 1.0 breeding calculator",
        "href": "/palworld-1-0-breeding-calculator/"
      },
      {
        "label": "1.0 breeding caveats",
        "href": "/guides/palworld-1-0-breeding-guide/"
      },
      {
        "label": "breeding FAQ",
        "href": "/guides/palworld-breeding-faq/"
      },
      {
        "label": "plan Selyne passives",
        "href": "/passive-skill-calculator/"
      },
      {
        "label": "data-source notes",
        "href": "/data-sources/"
      }
    ],
    "faqs": [
      {
        "question": "Can you breed Selyne in Palworld?",
        "answer": "Use PalCalculator's current breeding data to check Selyne parent-pair and route availability for the selected dataset."
      },
      {
        "question": "What parents make Selyne?",
        "answer": "Search Selyne in target-to-parent mode in the breeding calculator, then review current parent pairs and caveat labels."
      },
      {
        "question": "Is this updated for Palworld 1.0?",
        "answer": "The page should show the selected data version and route users to /data-sources/ so 1.0 and patch-sensitive assumptions remain visible."
      },
      {
        "question": "What if no Selyne route appears?",
        "answer": "Check spelling, add owned Pals, increase max generations, remove strict filters, and review data-source notes for unsupported areas."
      },
      {
        "question": "Should I trust one Selyne combo screenshot?",
        "answer": "Treat screenshots as leads only. Verify Selyne in the current calculator and compare patch timing before spending resources."
      }
    ]
  }
]
